import {
	DocumentData,
	collection,
	doc,
	getDoc,
	runTransaction,
	serverTimestamp,
	setDoc,
	updateDoc,
} from "firebase/firestore";
import {
	FC,
	Fragment,
	ReactNode,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import SignatureCanvas from "react-signature-canvas";

import DashboardLoading from "./DashboardLoading";
import { DOBSelect, adultBirthYears } from "./Athletes/Options";
import FormDropdown from "./Dropdown";
import Header from "../Header/Header";
import PayPopup from "./Events/PayPopup";
import RegisterPopup from "./Events/RegisterPopup";
import RegisterAthlete from "./RegisterAthlete";
import UnregisterPopup from "./Events/UnregisterPopup";
import TeamPracticeCalendar from "./TeamPracticeCalendar";
import { User } from "../../models/User";
import { authClasses } from "../../pages/Auth/authClasses";
import {
	db,
	createKidsLeagueTeam,
	getAdultRosterProfiles,
	joinKidsLeagueFreeAgentList,
	joinKidsLeagueTeam,
	leaveKidsLeagueFreeAgentList,
	sendAdultFreeAgentInvite,
	sendKidsLeagueFreeAgentInvite,
} from "../../firebase";
import {
	DashboardAthlete,
	getAthleteEventAgeLimitReason,
	getAvailableEvents,
	getEventDate,
	getUnpaidRegistrations,
	getVisiblePaidRegistrations,
	getVisibleEvents,
} from "./dashboardHelpers";
import {
	formatDateRange,
	formatEventTime,
	formatLeagueMatchTimes,
	getEventScheduleLabel,
	getSessionLabel,
	getSessionDisplayLabel,
	getRegisteredSessionDisplayLabel,
	isSessionInList,
} from "./eventSchedule";
import useDashboardData, {
	AdultLeagueMembershipSummary,
} from "./useDashboardData";

type EventFirstDashboardProps = {
	user: User;
};

type RegisterTarget = {
	athlete: DashboardAthlete;
	event: DocumentData;
} | null;

type AddEventRegistration = (
	eventID: string,
	athleteID: string,
	regID: string,
	status: string,
	practiceTeam: null | boolean,
	session: null | string,
) => void;

type AdultTeamRosterMember = {
	userID: string;
	isCaptain?: boolean;
	joinedAt?: Date;
};

type AdultLeagueTeam = {
	id: string;
	name: string;
	inviteID: string;
	freeAgentInvites?: {
		userID: string;
		invitedAt?: Date;
		invitedBy?: string;
	}[];
	roster?: AdultTeamRosterMember[];
	paidAt?: Date | { toDate: () => Date } | string | null;
	paymentRegistrationID?: string;
	createdBy?: string;
	createdAt?: Date;
};

type AdultFreeAgent = {
	userID: string;
	joinedAt?: Date;
};

type KidsLeagueRosterMember = {
	regID: string;
	athleteID: string;
	parentID: string;
	isCaptain?: boolean;
	joinedAt?: Date;
};

type KidsLeagueFreeAgent = {
	regID?: string;
	athleteID: string;
	parentID: string;
	athleteName?: string;
	joinedAt?: Date;
};

type KidsLeagueTeam = {
	id: string;
	name: string;
	inviteID: string;
	freeAgentInvites?: {
		athleteID: string;
		invitedAt?: Date;
		invitedBy?: string;
	}[];
	roster?: KidsLeagueRosterMember[];
	createdBy?: string;
	createdAt?: Date;
};

type AdultRosterProfile = {
	displayName?: string;
	firstName?: string;
	lastName?: string;
};

type AdultTeamRegistration = {
	event: DocumentData;
	team: AdultLeagueTeam;
};

type YouthTeamRosterMember = {
	regID?: string;
	athlete?: DashboardAthlete;
	discount?: number;
	payments?: {
		id?: string;
		amount?: number;
		date?: string;
		paymentType?: string;
		method?: string;
		note?: string;
	}[];
};

type YouthTeam = {
	id?: string;
	name: string;
	roster?: YouthTeamRosterMember[];
	dues?: number | string;
	headCoachUid?: string;
	assistantCoachUids?: string[];
	rosterPublished?: boolean;
};

type YouthTeamAssignment = {
	athlete: DashboardAthlete;
	event: DocumentData;
	member: YouthTeamRosterMember;
	registration: NonNullable<DashboardAthlete["registrations"]>[number];
	team: YouthTeam;
};

type DashboardPage = "current" | "registrations";
type ClinicFilter = "all" | "General" | "Advanced";

type EventSession = {
	title?: string;
	label?: string;
	startDate?: string;
	endDate?: string;
	startTime?: string;
	endTime?: string;
};

type HatchlingsMatchDay = {
	date?: string;
	startTime?: string;
};

const eventSections = [
	{
		label: "Club Tryouts",
		value: "Tryout",
		requiresChildAthlete: true,
	},
	{
		label: "Clinics",
		value: "Clinic",
		requiresChildAthlete: true,
	},
	{
		label: "Hatchlings",
		value: "Hatchlings",
		requiresChildAthlete: true,
	},
	{
		label: "Leagues",
		value: "League",
		requiresChildAthlete: false,
	},
];

const clinicFilters: { label: string; value: ClinicFilter }[] = [
	{ label: "All Clinics", value: "all" },
	{ label: "Beginner", value: "General" },
	{ label: "Advanced", value: "Advanced" },
];

const eventMatchesClinicFilter = (
	event: DocumentData,
	clinicFilter: ClinicFilter,
) => {
	if (event.type !== "Clinic" || clinicFilter === "all") {
		return true;
	}

	return event.subType === clinicFilter;
};

const getClinicBadgeLabel = (event: DocumentData) => {
	if (event.type !== "Clinic") {
		return "";
	}

	if (event.subType === "General") {
		return "Beginner";
	}

	return event.subType === "Advanced" ? "Advanced" : "";
};

const getSeasonBadgeLabel = (season: string | undefined) => {
	if (season === "Girls") {
		return "Girls Only";
	}

	if (season === "Boys") {
		return "Boys Only";
	}

	return "";
};

const getAdultLeagueFormatLabel = (season: string | undefined) => {
	if (season === "Boys" || season === "Men" || season === "Mens") {
		return "Men's League";
	}

	if (season === "Girls" || season === "Women" || season === "Womens") {
		return "Women's League";
	}

	return "Co-Ed League";
};

const getSeasonBadgeClass = (season: string | undefined) =>
	season === "Boys"
		? "border-accent-color-1/40 bg-[rgba(164,67,51,0.16)] text-accent-color-1"
		: "border-accent-color-2/30 bg-[rgba(108,207,246,0.12)] text-accent-color-2";

type AdultLeagueFactsProps = {
	event: DocumentData;
	teamName?: string;
	status?: string;
	fee?: string;
	className?: string;
};

const EventDescription: FC<{
	description?: string;
	fallback?: string;
}> = ({ description, fallback }) => {
	const text = `${description || fallback || ""}`
		.replace(/\\r\\n/g, "\n")
		.replace(/\\n/g, "\n")
		.replace(/\r\n/g, "\n")
		.replace(/\r/g, "\n");
	const paragraphs = text.split(/\n{2,}/).filter((paragraph) => paragraph.length);

	if (paragraphs.length === 0) {
		return null;
	}

	return (
		<div className="grid gap-3 text-sm leading-relaxed text-slate-300">
			{paragraphs.map((paragraph, paragraphIndex) => (
				<p className="m-0" key={`${paragraph}-${paragraphIndex}`}>
					{paragraph.split("\n").map((line, lineIndex) => (
						<Fragment key={`${line}-${lineIndex}`}>
							{lineIndex > 0 ? <br /> : null}
							{line}
						</Fragment>
					))}
				</p>
			))}
		</div>
	);
};

const getHatchlingsPracticeLabel = (event: DocumentData) => {
	const days = Array.isArray(event.hatchlingsPracticeDays)
		? event.hatchlingsPracticeDays.filter(Boolean).join(", ")
		: "";
	const time = formatEventTime(event.hatchlingsPracticeStartTime);
	if (!days && !time) {
		return "";
	}
	return [days || "Practice day TBD", time].filter(Boolean).join(" at ");
};

const getHatchlingsMatchLabels = (event: DocumentData) =>
	((event.hatchlingsMatchDays ?? []) as HatchlingsMatchDay[])
		.filter((matchDay) => matchDay.date || matchDay.startTime)
		.map((matchDay) =>
			[
				formatDateRange(matchDay.date, matchDay.date),
				formatEventTime(matchDay.startTime),
			]
				.filter(Boolean)
				.join(" at "),
		);

const parseEventDateString = (value: unknown, endOfDay = false) => {
	if (!value) {
		return null;
	}
	if (value instanceof Date) {
		const date = new Date(value);
		if (endOfDay) {
			date.setHours(23, 59, 59, 999);
		}
		return date;
	}
	if (
		typeof value === "object" &&
		value !== null &&
		"toDate" in value &&
		typeof (value as { toDate?: unknown }).toDate === "function"
	) {
		const date = (value as { toDate: () => Date }).toDate();
		if (endOfDay) {
			date.setHours(23, 59, 59, 999);
		}
		return date;
	}
	if (typeof value !== "string" && typeof value !== "number") {
		return null;
	}

	const dateParts =
		typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
			? value.split("-").map(Number)
			: null;
	const parsedDate = dateParts
		? new Date(
				dateParts[0],
				dateParts[1] - 1,
				dateParts[2],
				endOfDay ? 23 : 0,
				endOfDay ? 59 : 0,
				endOfDay ? 59 : 0,
				endOfDay ? 999 : 0,
			)
		: new Date(value);

	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isLeagueInProgress = (event: DocumentData) => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const startDate = parseEventDateString(event.startDate ?? event.date);
	const endDate = parseEventDateString(event.endDate ?? event.startDate ?? event.date, true);

	return Boolean(
		startDate &&
			endDate &&
			startDate.getTime() <= today.getTime() &&
			endDate.getTime() >= today.getTime(),
	);
};

const HatchlingsScheduleSummary: FC<{
	event: DocumentData;
	compact?: boolean;
}> = ({ event, compact = false }) => {
	if (event.type !== "Hatchlings") {
		return null;
	}

	const seasonDates = formatDateRange(event.startDate, event.endDate);
	const practiceLabel = getHatchlingsPracticeLabel(event);
	const matchLabels = getHatchlingsMatchLabels(event);

	if (!seasonDates && !practiceLabel && matchLabels.length === 0) {
		return null;
	}

	if (compact) {
		return (
			<div className={`mt-1 grid gap-0.5 ${adultMetaClass}`}>
				{seasonDates ? <span>Season: {seasonDates}</span> : null}
				{practiceLabel ? <span>Practice: {practiceLabel}</span> : null}
				{matchLabels.length > 0 ? (
					<span>
						Matches:{" "}
						{matchLabels.length > 2
							? `${matchLabels.slice(0, 2).join(", ")} + ${
									matchLabels.length - 2
								} more`
							: matchLabels.join(", ")}
					</span>
				) : null}
			</div>
		);
	}

	return (
		<div
			className="grid gap-1 rounded-md border border-accent-color-2/20 bg-accent-color-2/10 p-3 text-sm text-slate-200"
		>
			{seasonDates ? (
				<div>
					<span className="font-semibold text-white">Season:</span> {seasonDates}
				</div>
			) : null}
			{practiceLabel ? (
				<div>
					<span className="font-semibold text-white">Practice:</span>{" "}
					{practiceLabel}
				</div>
			) : null}
			{matchLabels.length > 0 ? (
				<div>
					<span className="font-semibold text-white">Matches:</span>{" "}
					{compact && matchLabels.length > 2
						? `${matchLabels.slice(0, 2).join(", ")} + ${
								matchLabels.length - 2
							} more`
						: matchLabels.join(", ")}
				</div>
			) : null}
		</div>
	);
};

const RegisteredBadge: FC<{ label?: string; tone?: "paid" | "due" }> = ({
	label = "Registered",
	tone = "paid",
}) => (
	<span
		className={`rounded-full border px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] ${
			tone === "due"
				? "border-yellow-300/30 bg-yellow-500/15 text-yellow-100"
				: "border-green-300/30 bg-green-500/15 text-green-100"
		}`}
	>
		{label}
	</span>
);

const RegistrationDetailLines: FC<{
	athlete: DashboardAthlete;
	event: DocumentData;
	registration: NonNullable<DashboardAthlete["registrations"]>[number];
}> = ({ athlete, event, registration }) => (
	<>
		<p className="m-0 mt-1 text-sm text-slate-300">
			{athlete.firstName} {athlete.lastName}
		</p>
		<div className={`mt-1 grid gap-0.5 ${adultMetaClass}`}>
			{registration.session ? <span>Session: {getRegisteredSessionDisplayLabel(event.sessions ?? [], registration.session)}</span> : null}
			{event.location ? <span>Location: {event.location}</span> : null}
		</div>
	</>
);

const AdultLeagueFacts: FC<AdultLeagueFactsProps> = ({
	event,
	teamName,
	status,
	fee,
	className = "",
}) => {
	const dateLabel = formatDateRange(event.startDate, event.endDate);
	const playDayLabel = event.leaguePlayDay ? `Every ${event.leaguePlayDay}` : "";
	const scheduleLabel = [playDayLabel, dateLabel].filter(Boolean).join(" ");
	const matchTimes = Array.isArray(event.leagueMatchTimes)
		? event.leagueMatchTimes
				.map((time: string) => formatEventTime(time))
				.filter(Boolean)
		: [];
	const matchTimesLabel = formatLeagueMatchTimes(event.leagueMatchTimes);
	const supportingDetails = [
		teamName ? { label: "Team", value: teamName } : null,
		event.location ? { label: "Location", value: event.location } : null,
		fee ? { label: "Fee", value: fee } : null,
	].filter(Boolean) as { label: string; value: string }[];

	if (
		!dateLabel &&
		!scheduleLabel &&
		!matchTimesLabel &&
		!status &&
		supportingDetails.length === 0
	) {
		return null;
	}

	return (
		<div
			className={`mt-2 grid gap-3 rounded-lg border border-white/10 bg-black/20 p-3 ${className}`}
		>
			<div className="flex flex-wrap items-start justify-between gap-2">
				{scheduleLabel ? (
					<div className="min-w-0 flex-1">
						<span className="block text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-500">
							Schedule
						</span>
						<span className="mt-1 block text-sm font-medium leading-snug text-slate-100">
							{scheduleLabel}
						</span>
					</div>
				) : null}
				{status ? (
					<span className="rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-200">
						{status}
					</span>
				) : null}
			</div>

			{matchTimes.length > 0 ? (
				<div className="grid gap-1.5">
					<span className="block text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-500">
						Possible Match Times
					</span>
					<div className="flex flex-wrap gap-1.5">
						{matchTimes.map((time) => (
							<span
								className="rounded-full border border-accent-color-2/25 bg-[rgba(108,207,246,0.1)] px-2.5 py-1 text-xs font-semibold text-slate-100"
								key={time}
							>
								{time}
							</span>
						))}
					</div>
				</div>
			) : null}

			{supportingDetails.length > 0 ? (
				<div className="grid gap-2 border-t border-white/10 pt-3 sm:grid-cols-3">
					{supportingDetails.map((item) => (
						<div className="min-w-0" key={item.label}>
							<span className="block text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-500">
								{item.label}
							</span>
							<span className="mt-1 block text-sm font-medium leading-snug text-slate-200">
								{item.value}
							</span>
						</div>
					))}
				</div>
			) : null}
		</div>
	);
};

const getSessionCapacity = (
	event: DocumentData,
	eventSession: EventSession,
	index: number,
) => {
	const sessionLabel = getSessionLabel(eventSession);
	const isSessionManuallySoldOut = isSessionInList(
		eventSession,
		event.manualSoldOutSessions ?? [],
	);
	const maxRegistrations = Number(event.maximumRegistrations);
	const hasRegistrationCap =
		event.maximumRegistrations != null &&
		event.maximumRegistrations !== "" &&
		Number.isFinite(maxRegistrations) &&
		maxRegistrations > 0;
	const sessionPaidCount = Array.isArray(event.sessionPaidArr)
		? Number(event.sessionPaidArr[index]) || 0
		: 0;
	const spotsRemaining = hasRegistrationCap
		? Math.max(maxRegistrations - sessionPaidCount, 0)
		: null;
	const isCapacitySoldOut = hasRegistrationCap && sessionPaidCount >= maxRegistrations;

	return {
		label: sessionLabel,
		isSoldOut: isSessionManuallySoldOut || isCapacitySoldOut,
		isSessionManuallySoldOut,
		spotsRemaining,
	};
};

const getEventCapacity = (event: DocumentData, session: string | null) => {
	if (event.multipleSessions) {
		const sessionStatuses = ((event.sessions ?? []) as EventSession[]).map(
			(eventSession, index) => getSessionCapacity(event, eventSession, index),
		);
		const selectedSessionStatus = sessionStatuses.find(
			(sessionStatus) => sessionStatus.label === session,
		);
		const allSessionsSoldOut =
			sessionStatuses.length > 0 &&
			sessionStatuses.every((sessionStatus) => sessionStatus.isSoldOut);

		return {
			isSoldOut: selectedSessionStatus?.isSoldOut ?? allSessionsSoldOut,
			spotsRemaining: selectedSessionStatus?.spotsRemaining ?? null,
		};
	}

	const maxRegistrations = Number(event.maximumRegistrations);
	const hasRegistrationCap =
		event.maximumRegistrations != null &&
		event.maximumRegistrations !== "" &&
		Number.isFinite(maxRegistrations) &&
		maxRegistrations > 0;
	const eventPaidCount = Number(event.paidRegs ?? event.paidRegistrations) || 0;
	const spotsRemaining = hasRegistrationCap
		? Math.max(maxRegistrations - eventPaidCount, 0)
		: null;
	const isCapacitySoldOut = hasRegistrationCap && eventPaidCount >= maxRegistrations;

	return {
		isSoldOut: event.soldOut === true || isCapacitySoldOut,
		spotsRemaining,
	};
};

const getEventCapacityWarningSpots = (
	event: DocumentData,
	selectedSession: string | null,
) => {
	if (!event.multipleSessions) {
		return getEventCapacity(event, selectedSession).spotsRemaining;
	}

	const warningSessionSpots = ((event.sessions ?? []) as EventSession[])
		.map((eventSession, index) => getSessionCapacity(event, eventSession, index))
		.filter(
			(sessionCapacity) =>
				!sessionCapacity.isSoldOut &&
				sessionCapacity.spotsRemaining != null &&
				sessionCapacity.spotsRemaining <= 5,
		)
		.map((sessionCapacity) => sessionCapacity.spotsRemaining as number);

	if (warningSessionSpots.length === 0) {
		return null;
	}

	return Math.min(...warningSessionSpots);
};

const isLeagueEvent = (event: DocumentData) => event.type === "League";

const getLeagueType = (event: DocumentData) =>
	isLeagueEvent(event) ? event.leagueType ?? "adult" : null;

const isAdultLeagueEvent = (event: DocumentData) =>
	isLeagueEvent(event) && getLeagueType(event) === "adult";

const isKidsLeagueEvent = (event: DocumentData) =>
	isLeagueEvent(event) && getLeagueType(event) === "kids";

const getEventRegistrationType = (event: DocumentData) =>
	event.registrationType ??
	(isAdultLeagueEvent(event) ? "adult" : "child");

const getTeamID = (team: AdultLeagueTeam) => team.id;

const getTeamInviteID = (team: AdultLeagueTeam) => team.inviteID;

const createTeamInviteID = (teamName: string) => {
	const slug =
		teamName
			.toLowerCase()
			.trim()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "") || "team";
	return `${slug}-${Math.random().toString(36).slice(2, 8)}`;
};

const createTeamID = () => `team-${Math.random().toString(36).slice(2, 12)}`;

const isTeamPaid = (team: AdultLeagueTeam) => Boolean(team.paidAt);

const isUserOnTeam = (team: AdultLeagueTeam, userID: string) =>
	(team.roster ?? []).some((member) => member.userID === userID);

const getKidsTeamID = (team: KidsLeagueTeam) => team.id;

const isAthleteOnKidsTeam = (team: KidsLeagueTeam, athleteID: string) =>
	(team.roster ?? []).some((member) => member.athleteID === athleteID);

const getKidsLeagueAthleteTeam = (
	teams: KidsLeagueTeam[],
	athleteID: string,
) => teams.find((team) => isAthleteOnKidsTeam(team, athleteID)) ?? null;

const isAthleteKidsFreeAgent = (
	freeAgents: KidsLeagueFreeAgent[],
	athleteID: string,
) => freeAgents.some((member) => member.athleteID === athleteID);

const isUserFreeAgent = (freeAgents: AdultFreeAgent[], userID: string) =>
	freeAgents.some((member) => member.userID === userID);

const getMinimumPlayersNeeded = (event: DocumentData, team: AdultLeagueTeam) => {
	const minimumTeamPlayers = Number(event.minimumTeamPlayers) || 0;
	return minimumTeamPlayers > 0
		? Math.max(minimumTeamPlayers - (team.roster?.length ?? 0), 0)
		: 0;
};

const getRosterMemberName = (
	member: AdultTeamRosterMember,
	rosterProfiles: Record<string, AdultRosterProfile> = {},
) => {
	const profile = rosterProfiles[member.userID];
	return (
		profile?.displayName ||
		`${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim() ||
		"Player"
	);
};

const inviteLinkButtonClass =
	"my-1 border-0 bg-transparent p-0 text-xs font-semibold text-accent-color-2 underline underline-offset-4 transition hover:text-white";
const adultStatusBadgeClass =
	"rounded-full border px-2.5 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em]";
const adultSectionLabelClass =
	"text-xs font-semibold uppercase tracking-[0.12em] text-slate-400";
const adultMetaClass = "text-xs leading-snug text-slate-500";
const leaguesUrl = "https://leagues.sidewinderva.com";
const hatchlingsUrl = "https://sidewinderva.com/hatchlings";
const adultLeagueWaiverVersion = "adult-league-waiver-2026-07-28";
const adultLeagueWaiverTitle =
	"Adult League Waiver, Release of Liability, Assumption of Risk, and Indemnity Agreement";
const adultLeagueWaiverText = [
	"I am voluntarily registering to participate in an adult volleyball league, practice, match, open gym, clinic, tournament, or related activity operated, hosted, sponsored, or organized by Sidewinder Volleyball Academy and/or Sidewinder VA LLC.",
	"I understand that volleyball and related athletic activities involve inherent and foreseeable risks, including falls, collisions, equipment contact, sprains, strains, fractures, concussions, head, neck, back, knee, ankle, shoulder, or other injuries, dehydration, overexertion, illness, serious injury, permanent disability, or death.",
	"I understand that these risks may arise from my own actions or inactions, the actions or inactions of other participants, the condition of the facility or equipment, or the ordinary negligence of Sidewinder, its owners, directors, officers, employees, contractors, coaches, volunteers, agents, sponsors, facility owners, and related parties.",
	"I certify that I am physically able to participate and that I am responsible for deciding whether I am healthy enough to participate. I agree to stop participating if I feel unsafe, injured, ill, or unable to continue.",
	"I voluntarily assume all risks associated with participation, whether known or unknown, foreseeable or unforeseeable.",
	"To the fullest extent permitted by Arizona law, I release, waive, discharge, and agree not to sue Sidewinder Volleyball Academy, Sidewinder VA LLC, and their owners, directors, officers, employees, contractors, coaches, volunteers, agents, sponsors, facility owners, and related parties from claims, demands, losses, damages, costs, or causes of action arising out of or related to my participation, including claims based on ordinary negligence.",
	"This release does not apply to claims that cannot legally be waived under Arizona law, including claims based on gross negligence, willful or wanton misconduct, reckless conduct, or intentional misconduct.",
	"I agree to indemnify and hold harmless Sidewinder Volleyball Academy, Sidewinder VA LLC, and their owners, directors, officers, employees, contractors, coaches, volunteers, agents, sponsors, facility owners, and related parties from claims, losses, damages, costs, or expenses, including reasonable attorney fees, arising out of my actions, misconduct, or violation of league rules.",
	"I agree to follow all posted rules, league rules, facility rules, and instructions from Sidewinder staff or representatives. I understand that Sidewinder may remove me from participation for unsafe, disruptive, abusive, or inappropriate behavior.",
	"In the event of an emergency, I authorize Sidewinder staff, coaches, volunteers, or facility personnel to seek emergency medical assistance for me. I understand that I am responsible for any medical expenses incurred.",
	"I understand that this agreement is intended to be as broad and inclusive as permitted by Arizona law. If any portion is found unenforceable, the remaining portions will remain in effect.",
	"I acknowledge that I have read this agreement, understand that I am giving up substantial legal rights, and sign it freely and voluntarily.",
	"By checking the agreement box and signing below, I agree that my electronic signature has the same legal effect as a handwritten signature. I consent to Sidewinder storing this signed waiver electronically.",
];

const getLeagueUrl = (event: DocumentData) =>
	event.leagueUrl ?? `${leaguesUrl}/league/${event.id}`;

const getAdultLeagueWaiverID = (eventID: string, userID: string) =>
	`${eventID}_${userID}_${adultLeagueWaiverVersion}`;
const getAdultLeagueGlobalWaiverID = (userID: string) =>
	`${userID}_${adultLeagueWaiverVersion}`;

const getAdultFullName = (userData: DocumentData) =>
	`${userData.firstName ?? ""} ${userData.lastName ?? ""}`.trim();

const hasSignedAdultLeagueWaiver = async (
	userID: string,
	adultEvents: DocumentData[],
) => {
	const globalWaiverSnap = await getDoc(
		doc(db, "adultLeagueWaivers", getAdultLeagueGlobalWaiverID(userID)),
	);
	if (globalWaiverSnap.exists()) {
		return true;
	}

	const eventWaiverSnaps = await Promise.all(
		adultEvents.map((event) =>
			getDoc(
				doc(db, "adultLeagueWaivers", getAdultLeagueWaiverID(event.id, userID)),
			),
		),
	);
	return eventWaiverSnaps.some((waiverSnap) => waiverSnap.exists());
};

type AdultLeagueWaiverModalProps = {
	event?: DocumentData | null;
	onCancel: () => void;
	onSigned: () => void;
	user: User;
	userData: DocumentData;
};

const AdultLeagueWaiverModal: FC<AdultLeagueWaiverModalProps> = ({
	event,
	onCancel,
	onSigned,
	user,
	userData,
}) => {
	const signatureRef = useRef<SignatureCanvas | null>(null);
	const [printedName, setPrintedName] = useState(getAdultFullName(userData));
	const [typedSignature, setTypedSignature] = useState(
		getAdultFullName(userData),
	);
	const [acceptedElectronicSignature, setAcceptedElectronicSignature] =
		useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	const saveWaiver = async () => {
		setError("");
		const trimmedName = printedName.trim();
		if (!trimmedName) {
			setError("Enter your full legal name.");
			return;
		}
		if (!acceptedElectronicSignature) {
			setError("Confirm that you agree to sign electronically.");
			return;
		}
		const trimmedSignature = typedSignature.trim();
		const hasDrawnSignature =
			Boolean(signatureRef.current) && !signatureRef.current?.isEmpty();
		if (!trimmedSignature && !hasDrawnSignature) {
			setError("Type your signature or sign in the signature box.");
			return;
		}

		setSaving(true);
		try {
			const signatureImage = hasDrawnSignature
				? signatureRef.current?.getCanvas().toDataURL("image/png") ?? null
				: null;
			await setDoc(
				doc(
					db,
					"adultLeagueWaivers",
					event?.id
						? getAdultLeagueWaiverID(event.id, user.id)
						: getAdultLeagueGlobalWaiverID(user.id),
				),
				{
					acceptedElectronicSignature: true,
					appliesTo: "adultLeagues",
					email: user.email,
					eventID: event?.id ?? null,
					eventTitle: event?.title ?? "Adult Leagues",
					fullName: trimmedName,
					signatureImage,
					signatureMethod: signatureImage ? "drawn" : "typed",
					typedSignature: trimmedSignature || trimmedName,
					signedAt: serverTimestamp(),
					source: "dashboard",
					userAgent: window.navigator.userAgent,
					userID: user.id,
					waiverText: adultLeagueWaiverText,
					waiverTitle: adultLeagueWaiverTitle,
					waiverVersion: adultLeagueWaiverVersion,
				},
			);
			onSigned();
		} catch (error: any) {
			setError(error.message ?? "Could not save waiver. Try again.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="fixed inset-0 z-30 flex items-center justify-center overflow-y-auto bg-black/80 px-4 py-6">
			<div className="grid max-h-[92vh] w-full max-w-3xl gap-4 overflow-y-auto rounded-lg border border-white/10 bg-[#24262c] p-5 shadow-2xl shadow-black/50">
				<div>
					<span className="text-xs font-semibold uppercase tracking-[0.14em] text-accent-color-2">
						Required Waiver
					</span>
					<h3 className="m-0 mt-1 text-2xl text-white">
						{adultLeagueWaiverTitle}
					</h3>
					<p className="m-0 mt-2 text-sm text-slate-300">
						Sign once before joining a team, creating a team, joining the
						free-agent list, or paying for an adult league team.
					</p>
				</div>

				<div className="max-h-60 overflow-y-auto rounded-md border border-white/10 bg-black/25 p-3 text-sm leading-relaxed text-slate-300">
					{adultLeagueWaiverText.map((paragraph, index) => (
						<p className="m-0 mb-3 last:mb-0" key={index}>
							{paragraph}
						</p>
					))}
				</div>

				<label className="grid gap-1.5">
					<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
						Full Legal Name
					</small>
					<input
						className="h-11 rounded-md border border-white/10 bg-black/30 px-3 text-sm text-white outline-none focus:border-accent-color-2"
						value={printedName}
						onChange={(e) => setPrintedName(e.target.value)}
					/>
				</label>

				<div className="grid gap-2">
					<div>
						<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
							Drawn Signature
						</small>
						<p className="m-0 mt-1 text-xs text-slate-400">
							Draw your signature below, or type your signature if the box does
							not work on your device.
						</p>
					</div>
					<div className="overflow-hidden rounded-md border border-white/10 bg-white">
						<SignatureCanvas
							ref={signatureRef}
							canvasProps={{
								className: "block h-40 w-full touch-none",
							}}
							penColor="#101216"
						/>
					</div>
					<button
						className="justify-self-start border-0 bg-transparent p-0 text-xs font-semibold text-accent-color-2 underline underline-offset-4"
						type="button"
						onClick={() => signatureRef.current?.clear()}
					>
						Clear signature
					</button>
				</div>

				<label className="grid gap-1.5">
					<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
						Typed Signature
					</small>
					<input
						className="h-11 rounded-md border border-white/10 bg-black/30 px-3 text-sm text-white outline-none focus:border-accent-color-2"
						value={typedSignature}
						onChange={(e) => setTypedSignature(e.target.value)}
					/>
					<small className="text-xs text-slate-400">
						Typing your name here also counts as your electronic signature.
					</small>
				</label>

				<label className="flex items-start gap-3 text-sm text-slate-300">
					<input
						className="mt-1 h-4 w-4 shrink-0 accent-accent-color-2"
						checked={acceptedElectronicSignature}
						type="checkbox"
						onChange={(e) => setAcceptedElectronicSignature(e.target.checked)}
					/>
					<span>
						I agree that my electronic signature has the same legal effect as a
						handwritten signature.
					</span>
				</label>

				{error ? (
					<div className="rounded-md border border-yellow-300/30 bg-yellow-500/10 p-3 text-sm text-yellow-100">
						{error}
					</div>
				) : null}

				<div className="flex flex-wrap justify-end gap-3 border-t border-white/10 pt-4">
					<button
						className={`${authClasses.buttonSmall} border-white/10 bg-[#333333] hover:bg-[#3f3f3f]`}
						disabled={saving}
						type="button"
						onClick={onCancel}
					>
						Cancel
					</button>
					<button
						className={authClasses.buttonSmall}
						disabled={saving}
						type="button"
						onClick={saveWaiver}
					>
						{saving ? "Saving..." : "Sign and Continue"}
					</button>
				</div>
			</div>
		</div>
	);
};

const clearInviteParams = () => {
	const url = new URL(window.location.href);
	url.searchParams.delete("league");
	url.searchParams.delete("team");
	window.history.replaceState({}, "", `${url.pathname}${url.search}`);
};

const copyTextToClipboard = async (text: string) => {
	if (navigator.clipboard?.writeText) {
		await navigator.clipboard.writeText(text);
		return;
	}

	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "fixed";
	textarea.style.left = "-9999px";
	document.body.appendChild(textarea);
	textarea.select();
	const copied = document.execCommand("copy");
	document.body.removeChild(textarea);

	if (!copied) {
		throw new Error("copy-failed");
	}
};

const getYouthTeamAssignments = (
	visibleEvents: DocumentData[],
	athletes: DashboardAthlete[],
) => {
	const registrationMap = new Map<
		string,
		{
			athlete: DashboardAthlete;
			registration: NonNullable<DashboardAthlete["registrations"]>[number];
		}
	>();
	athletes.forEach((athlete) => {
		(athlete.registrations ?? []).forEach((registration) => {
			registrationMap.set(registration.regID, { athlete, registration });
		});
	});

	return visibleEvents
			.filter((event) => event.type === "Tryout")
			.flatMap((event) =>
				((event.teams ?? []) as YouthTeam[]).filter(
					(team) => team.rosterPublished === true,
				).flatMap((team) =>
					(team.roster ?? [])
					.map<YouthTeamAssignment | null>((member) => {
						if (isOldFullyPaidYouthTeam(event, team, member)) {
							return null;
						}
						if (
							event.hidden === true &&
							!hasYouthTeamDuesSet(team)
						) {
							return null;
						}
						if (!member.regID) {
							return null;
						}
						const registrationMatch = registrationMap.get(member.regID);
						if (!registrationMatch) {
							return null;
						}
						return {
							...registrationMatch,
							event,
							member,
							team,
						};
					})
					.filter(
						(assignment): assignment is YouthTeamAssignment =>
							assignment !== null,
					),
			),
		)
		.sort((a, b) => {
			const dateDiff =
				(getEventDate(b.event)?.getTime() ?? 0) -
				(getEventDate(a.event)?.getTime() ?? 0);
			if (dateDiff !== 0) {
				return dateDiff;
			}
			return `${a.team.name}`.localeCompare(`${b.team.name}`);
		});
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 0,
	maximumFractionDigits: 2,
});

const formatCurrency = (value: number) => currencyFormatter.format(value);

const getPaymentTotal = (member: YouthTeamRosterMember) =>
	(member.payments ?? []).reduce(
		(total, payment) => total + (Number(payment.amount) || 0),
		0,
	);

const hasYouthTeamDuesSet = (team: YouthTeam) => Number(team.dues) > 0;

const getYouthTeamBalance = (team: YouthTeam, member: YouthTeamRosterMember) => {
	const dues = Number(team.dues) || 0;
	const discount = Number(member.discount) || 0;
	return dues - discount - getPaymentTotal(member);
};

const isFullyPaidYouthTeam = (
	team: YouthTeam,
	member: YouthTeamRosterMember,
) => hasYouthTeamDuesSet(team) && getYouthTeamBalance(team, member) <= 0;

const isEventOlderThanTenMonths = (event: DocumentData) => {
	const eventDate = getEventDate(event);
	if (!eventDate) {
		return false;
	}
	const cutoff = new Date();
	cutoff.setMonth(cutoff.getMonth() - 10);
	return eventDate < cutoff;
};

const isOldFullyPaidYouthTeam = (
	event: DocumentData,
	team: YouthTeam,
	member: YouthTeamRosterMember,
) => isEventOlderThanTenMonths(event) && isFullyPaidYouthTeam(team, member);

const formatPaymentDate = (value: unknown) => {
	if (!value) {
		return "";
	}

	if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
		const [year, month, day] = value.split("-").map(Number);
		return new Date(year, month - 1, day).toLocaleDateString();
	}

	if (
		typeof value === "object" &&
		value !== null &&
		"toDate" in value &&
		typeof (value as { toDate?: unknown }).toDate === "function"
	) {
		return (value as { toDate: () => Date }).toDate().toLocaleDateString();
	}

	const date = new Date(String(value));
	return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
};

const getBalanceStatus = (balance: number, totalPaid: number) => {
	if (balance < 0) {
		return "Paid";
	}
	if (balance === 0) {
		return "Paid";
	}
	return totalPaid > 0 ? "Partial" : "Unpaid";
};

const getDateFromUserData = (value: unknown) => {
	if (!value) {
		return null;
	}
	if (value instanceof Date) {
		return value;
	}
	if (
		typeof value === "object" &&
		value !== null &&
		"toDate" in value &&
		typeof (value as { toDate?: unknown }).toDate === "function"
	) {
		return (value as { toDate: () => Date }).toDate();
	}
	return null;
};

const monthNames = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

const buildDOB = (month: string, day: string, year: string) => {
	const monthIndex = monthNames.indexOf(month);
	const parsedDay = Number(day);
	const parsedYear = Number(year);

	if (
		monthIndex < 0 ||
		!Number.isInteger(parsedDay) ||
		!Number.isInteger(parsedYear)
	) {
		return null;
	}

	const dob = new Date(parsedYear, monthIndex, parsedDay);
	if (
		dob.getFullYear() !== parsedYear ||
		dob.getMonth() !== monthIndex ||
		dob.getDate() !== parsedDay
	) {
		return null;
	}

	return dob;
};

const isAdultProfileDataComplete = (userData: DocumentData | null) =>
	Boolean((userData?.firstName ?? "").trim()) &&
	Boolean((userData?.lastName ?? "").trim()) &&
	String(userData?.phone ?? "").replace(/\D/g, "").length === 10 &&
	Boolean(userData?.gender) &&
	userData?.gender !== "Gender" &&
	getDateFromUserData(userData?.dob) !== null;

type AdultProfileGateProps = {
	onProfileSaved: () => void | Promise<void>;
	user: User;
	userData: DocumentData | null;
};

const AdultProfileGate: FC<AdultProfileGateProps> = ({
	onProfileSaved,
	user,
	userData,
}) => {
	const { buttonSmall } = authClasses;
	const existingDOB = getDateFromUserData(userData?.dob);
	const [profileSaving, setProfileSaving] = useState(false);
	const [profileError, setProfileError] = useState("");
	const [profileSuccess, setProfileSuccess] = useState("");
	const [firstName, setFirstName] = useState(userData?.firstName ?? "");
	const [lastName, setLastName] = useState(userData?.lastName ?? "");
	const [phone, setPhone] = useState(userData?.phone ?? "");
	const [gender, setGender] = useState(userData?.gender ?? "Gender");
	const [month, setMonth] = useState(
		existingDOB
			? existingDOB.toLocaleString("default", { month: "long" })
			: "Month",
	);
	const [day, setDay] = useState(
		existingDOB ? String(existingDOB.getDate()) : "Day",
	);
	const [year, setYear] = useState(
		existingDOB ? String(existingDOB.getFullYear()) : "Year",
	);
	const dob = buildDOB(month, day, year);
	const trimmedFirstName = firstName.trim();
	const trimmedLastName = lastName.trim();
	const normalizedPhone = phone.replace(/\D/g, "").slice(0, 10);
	const adultProfileComplete =
		trimmedFirstName !== "" &&
		trimmedLastName !== "" &&
		normalizedPhone.length === 10 &&
		gender !== "Gender" &&
		dob !== null;
	const missingAdultProfileMessage =
		trimmedFirstName === ""
			? "Missing first name"
			: trimmedLastName === ""
				? "Missing last name"
				: normalizedPhone.length !== 10
					? "Enter a 10-digit phone number"
					: gender === "Gender"
						? "Select gender"
						: dob === null
							? "Incomplete date of birth"
							: "";

	const handleSaveAdultProfile = async () => {
		setProfileError("");
		setProfileSuccess("");
		if (!adultProfileComplete || dob === null) {
			setProfileError(missingAdultProfileMessage);
			return;
		}

		setProfileSaving(true);
		try {
			await updateDoc(doc(db, "users", user.id), {
				firstName: trimmedFirstName,
				lastName: trimmedLastName,
				phone: normalizedPhone,
				gender,
				dob,
			});
			setProfileSuccess("Your information was saved.");
			await onProfileSaved();
		} catch {
			setProfileError("Could not save your information. Try again.");
		} finally {
			setProfileSaving(false);
		}
	};

	return (
		<div className="grid gap-4 rounded-lg border border-white/10 bg-white/[0.03] p-5">
			<div>
				<h3 className="m-0 text-xl text-white">Complete Your Information</h3>
				<p className="m-0 mt-1 text-sm text-slate-400">
					This is required before viewing adult league registration options.
				</p>
			</div>
			<div className="grid gap-3 md:grid-cols-2">
				<input
					className="h-10 rounded-md border border-white/10 bg-black/30 px-3 text-sm text-white outline-none focus:border-accent-color-2"
					placeholder="First name"
					value={firstName}
					onChange={(e) => setFirstName(e.target.value)}
				/>
				<input
					className="h-10 rounded-md border border-white/10 bg-black/30 px-3 text-sm text-white outline-none focus:border-accent-color-2"
					placeholder="Last name"
					value={lastName}
					onChange={(e) => setLastName(e.target.value)}
				/>
				<input
					className="h-10 rounded-md border border-white/10 bg-black/30 px-3 text-sm text-white outline-none focus:border-accent-color-2"
					placeholder="Phone number"
					value={phone}
					inputMode="tel"
					onChange={(e) =>
						setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
					}
				/>
				<FormDropdown
					options={["Gender", "Female", "Male", "Non-binary"]}
					value={gender}
					setVal={setGender}
				/>
			</div>
			<div className="grid gap-1.5">
				<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
					Date of Birth
				</small>
				<DOBSelect
					day={day}
					setDay={setDay}
					month={month}
					setMonth={setMonth}
					year={year}
					setYear={setYear}
					yearOptions={adultBirthYears}
				/>
			</div>
			<div className="flex flex-wrap items-center gap-3">
				<button
					className={buttonSmall}
					disabled={profileSaving}
					type="button"
					onClick={handleSaveAdultProfile}
				>
					{profileSaving ? "Saving..." : "Save Information"}
				</button>
				{profileError ? (
					<small className="text-yellow-100">{profileError}</small>
				) : null}
				{profileSuccess ? (
					<small className="text-accent-color-2">{profileSuccess}</small>
				) : null}
			</div>
		</div>
	);
};

type AdultLeagueWaiverGateProps = {
	adultEvents: DocumentData[];
	children: ReactNode;
	user: User;
	userData: DocumentData;
};

const AdultLeagueWaiverGate: FC<AdultLeagueWaiverGateProps> = ({
	adultEvents,
	children,
	user,
	userData,
}) => {
	const [checkingWaiver, setCheckingWaiver] = useState(true);
	const [waiverSigned, setWaiverSigned] = useState(false);
	const [waiverModalOpen, setWaiverModalOpen] = useState(false);
	const [waiverError, setWaiverError] = useState("");
	const adultEventIDs = adultEvents.map((event) => event.id).join("|");

	useEffect(() => {
		let active = true;
		const loadWaiverStatus = async () => {
			setCheckingWaiver(true);
			setWaiverError("");
			try {
				const signed = await hasSignedAdultLeagueWaiver(user.id, adultEvents);
				if (active) {
					setWaiverSigned(signed);
				}
			} catch {
				if (active) {
					setWaiverSigned(false);
					setWaiverError("Could not check waiver status. Try again.");
				}
			} finally {
				if (active) {
					setCheckingWaiver(false);
				}
			}
		};

		loadWaiverStatus();

		return () => {
			active = false;
		};
	}, [adultEventIDs, adultEvents, user.id]);

	if (checkingWaiver) {
		return (
			<div className="rounded-lg border border-white/10 bg-white/[0.03] p-5 text-sm text-slate-300">
				Checking adult league waiver status...
			</div>
		);
	}

	if (waiverSigned) {
		return <>{children}</>;
	}

	return (
		<div className="grid gap-4 rounded-lg border border-white/10 bg-white/[0.03] p-5">
			{waiverModalOpen ? (
				<AdultLeagueWaiverModal
					event={null}
					onCancel={() => setWaiverModalOpen(false)}
					onSigned={() => {
						setWaiverSigned(true);
						setWaiverModalOpen(false);
					}}
					user={user}
					userData={userData}
				/>
			) : null}
			<div>
				<h3 className="m-0 text-xl text-white">Sign Adult League Waiver</h3>
				<p className="m-0 mt-1 text-sm text-slate-400">
					This is required before viewing adult league registration options,
					joining the free-agent list, creating a team, joining a team, or
					paying a team fee.
				</p>
			</div>
			<div className="flex flex-wrap items-center gap-3">
				<button
					className={authClasses.buttonSmall}
					type="button"
					onClick={() => setWaiverModalOpen(true)}
				>
					Review and Sign Waiver
				</button>
				{waiverError ? (
					<small className="text-yellow-100">{waiverError}</small>
				) : null}
			</div>
		</div>
	);
};

const getAgeLimitLabel = (event: DocumentData) => {
	const minAge = Number(event.minAge);
	const maxAge = Number(event.maxAge);
	const hasMinAge =
		event.minAge != null &&
		event.minAge !== "" &&
		Number.isFinite(minAge) &&
		minAge >= 0;
	const hasMaxAge =
		event.maxAge != null &&
		event.maxAge !== "" &&
		Number.isFinite(maxAge) &&
		maxAge >= 0;

	if (hasMinAge && hasMaxAge) {
		return minAge === maxAge ? `Age ${minAge}` : `Ages ${minAge}-${maxAge}`;
	}
	if (hasMinAge) {
		return `Ages ${minAge}+`;
	}
	if (hasMaxAge) {
		return `Ages ${maxAge} and under`;
	}
	return "";
};

const getCapacityWarningLabel = (spotsRemaining: number | null) => {
	if (spotsRemaining == null || spotsRemaining > 5) {
		return "";
	}

	return spotsRemaining === 1 ? "1 spot remaining" : "Almost Full";
};

const getCapacityWarningClass = (spotsRemaining: number | null) =>
	spotsRemaining === 1
		? "border-red-300/30 bg-red-500/15 text-red-100"
		: "border-yellow-300/30 bg-yellow-500/15 text-yellow-100";

type EventCardProps = {
	event: DocumentData;
	athletes: DashboardAthlete[];
	onRegister: (target: RegisterTarget) => void;
};

const EventFirstCard: FC<EventCardProps> = ({ event, athletes, onRegister }) => {
	const { buttonSmall } = authClasses;
	const [expanded, setExpanded] = useState(false);
	const clinicBadge = getClinicBadgeLabel(event);
	const seasonBadge = getSeasonBadgeLabel(event.season);
	const seasonBadgeClass = getSeasonBadgeClass(event.season);
	const ageLimitLabel = getAgeLimitLabel(event);
	const eligibleAthletes = athletes.filter(
		(athlete) => getAvailableEvents(athlete, [event]).length > 0,
	);
	const registeredAthletes = athletes.filter((athlete) =>
		athlete.registrations?.some((registration) => registration.id === event.id),
	);
	const [selectedAthleteID, setSelectedAthleteID] = useState(
		eligibleAthletes.find(
			(athlete) => getAthleteEventAgeLimitReason(athlete, event) === "",
		)?.id ??
			eligibleAthletes[0]?.id ??
			"",
	);
	const firstAvailableSession = event.multipleSessions
		? getSessionLabel(
				event.sessions?.find(
					(eventSession: EventSession, index: number) =>
						!getSessionCapacity(event, eventSession, index).isSoldOut,
				) ??
					event.sessions?.[0] ??
					{},
			) || null
		: null;
	const [session, setSession] = useState(firstAvailableSession);
	const selectedAthlete = eligibleAthletes.find(
		(athlete) => athlete.id === selectedAthleteID,
	);
	const selectedAthleteAgeLimitReason = selectedAthlete
		? getAthleteEventAgeLimitReason(selectedAthlete, event)
		: "";
	const capacity = getEventCapacity(event, session);
	const capacityWarningSpots = getEventCapacityWarningSpots(event, session);
	const capacityWarningLabel = getCapacityWarningLabel(capacityWarningSpots);
	const sessionOptions =
		event.sessions?.map(
			(eventSession: EventSession, index: number) => {
				const sessionLabel = getSessionLabel(eventSession);
				const sessionDisplay = getSessionDisplayLabel(eventSession);
				const sessionCapacity = getSessionCapacity(event, eventSession, index);
				const { isSessionManuallySoldOut, spotsRemaining } = sessionCapacity;
				const label =
					isSessionManuallySoldOut
						? `${sessionDisplay} - Sold Out`
					: spotsRemaining == null
					? sessionDisplay
					: spotsRemaining === 0
						? `${sessionDisplay} - Sold Out`
						: spotsRemaining <= 5
							? `${sessionDisplay} - ${getCapacityWarningLabel(spotsRemaining)}`
							: sessionDisplay;

				return {
					value: sessionLabel,
					label,
					disabled: sessionCapacity.isSoldOut,
				};
			}) ?? [];
	const athleteOptions = eligibleAthletes.map((athlete) => {
		const ageLimitReason = getAthleteEventAgeLimitReason(athlete, event);
		return {
			value: athlete.id,
			label: `${athlete.firstName} ${athlete.lastName}${
				ageLimitReason ? ` - ${ageLimitReason}` : ""
			}`,
			disabled: ageLimitReason !== "",
		};
	});
	const disabledReason = !event.open
		? event.openString || "Registration is not open yet."
		: capacity.isSoldOut
			? "This event is sold out."
			: eligibleAthletes.length === 0
				? "No eligible athletes available on this account."
				: "";
	const leadCoachName = event.leadCoachName || event.leadCoach;

	return (
		<div
			className={`grid gap-3 rounded-lg border p-3 ${
				event.open
					? "border-white/10 bg-white/[0.03]"
					: "border-white/10 bg-black/20 opacity-80"
			}`}
		>
			<div className="grid gap-3">
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<h3 className="m-0 text-base text-white">{event.title}</h3>
						{registeredAthletes.length > 0 ? <RegisteredBadge /> : null}
						{!event.open ? (
							<span className="rounded-full border border-white/10 bg-white/10 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-300">
								Not Open
							</span>
						) : null}
						{capacity.isSoldOut ? (
							<span className="rounded-full border border-red-300/30 bg-red-500/15 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-red-100">
								Sold Out
							</span>
						) : null}
						{event.open &&
						!capacity.isSoldOut &&
						capacityWarningLabel ? (
							<span
								className={`rounded-full border px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] ${getCapacityWarningClass(
									capacityWarningSpots,
								)}`}
							>
								{capacityWarningLabel}
							</span>
						) : null}
					</div>
					{event.type === "Clinic" && leadCoachName ? (
						<p className="m-0 mt-1 text-sm text-slate-300">
							Lead Coach: {leadCoachName}
						</p>
					) : null}
					<p className="m-0 mt-1 text-sm text-slate-400">
						{getEventScheduleLabel(event)} · {event.location} · ${event.cost}
					</p>
					{registeredAthletes.length > 0 ? (
						<p className="m-0 mt-1 text-sm font-semibold text-green-100">
							{registeredAthletes
								.map((athlete) => `${athlete.firstName} ${athlete.lastName}`)
								.join(", ")}{" "}
							{registeredAthletes.length === 1 ? "is" : "are"} already registered.
						</p>
					) : null}
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{clinicBadge ? (
						<span className="rounded-full border border-yellow-300/30 bg-yellow-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-yellow-100">
							{clinicBadge}
						</span>
					) : null}
					{seasonBadge ? (
						<span
							className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${seasonBadgeClass}`}
						>
							{seasonBadge}
						</span>
					) : null}
					{ageLimitLabel ? (
						<span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-300">
							{ageLimitLabel}
						</span>
					) : null}
					<button
						className="rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm font-bold text-white transition hover:border-accent-color-2 hover:text-accent-color-2"
						type="button"
						onClick={() => setExpanded((current) => !current)}
					>
						{expanded ? "Less" : "Details"}
					</button>
				</div>
			</div>

				{expanded ? (
					<div className="grid gap-3 border-t border-white/10 pt-3">
						<EventDescription description={event.description} />
						<HatchlingsScheduleSummary event={event} />

					{registeredAthletes.length > 0 ? (
						<p className="m-0 text-sm text-slate-400">
							Already registered:{" "}
							<span className="text-slate-200">
								{registeredAthletes
									.map((athlete) => `${athlete.firstName} ${athlete.lastName}`)
									.join(", ")}
							</span>
						</p>
					) : null}

					{disabledReason ? (
						<div className="rounded-md border border-white/10 bg-black/20 p-3 text-sm text-slate-300">
							{disabledReason}
						</div>
					) : (
						<div className="grid gap-3 rounded-md border border-white/10 bg-black/20 p-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
							<div className="grid gap-1.5">
								<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
									Athlete
								</small>
								<FormDropdown
									value={selectedAthleteID}
									options={athleteOptions}
									setVal={setSelectedAthleteID}
								/>
							</div>
							{event.multipleSessions ? (
								<div className="grid gap-1.5">
									<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
										Session
									</small>
									<FormDropdown
										value={session ?? ""}
										options={sessionOptions}
										setVal={setSession}
									/>
								</div>
							) : (
								<div className="hidden md:block" />
							)}
							<button
								className={buttonSmall}
								disabled={!selectedAthlete || selectedAthleteAgeLimitReason !== ""}
								onClick={() =>
									selectedAthlete && selectedAthleteAgeLimitReason === ""
										? onRegister({
												athlete: selectedAthlete,
												event: { ...event, session },
											})
										: null
								}
							>
								Register
							</button>
							{selectedAthleteAgeLimitReason ? (
								<small className="text-yellow-100 md:col-span-3">
									This athlete is {selectedAthleteAgeLimitReason} for this event.
								</small>
							) : null}
						</div>
					)}
				</div>
			) : null}
		</div>
	);
	};

type KidsLeagueEventCardProps = {
	addEventRegistration: AddEventRegistration;
	athletes: DashboardAthlete[];
	event: DocumentData;
	inviteAthleteID: string | null;
	inviteTeamID: string | null;
	onEventChange: (event: DocumentData) => void;
	removeEventRegistration: (athleteID: string, regID: string) => void;
	user: User;
};

type KidsLeagueActionResponse = {
	event?: DocumentData;
	registration?: {
		athleteID: string;
		created?: boolean;
		eventID: string;
		regID: string;
		removed?: boolean;
		status?: string;
	} | null;
	teamName?: string;
};

const KidsLeagueEventCard: FC<KidsLeagueEventCardProps> = ({
	addEventRegistration,
	athletes,
	event,
	inviteAthleteID,
	inviteTeamID,
	onEventChange,
	removeEventRegistration,
	user,
}) => {
	const [expanded, setExpanded] = useState(Boolean(inviteTeamID));
	const [teamName, setTeamName] = useState("");
	const [selectedAthleteID, setSelectedAthleteID] = useState("");
	const [savingTeam, setSavingTeam] = useState(false);
	const [actionError, setActionError] = useState("");
	const [actionSuccess, setActionSuccess] = useState("");
	const [copiedTeamID, setCopiedTeamID] = useState("");
	const [sendingInviteKey, setSendingInviteKey] = useState("");
	const [sentInviteKeys, setSentInviteKeys] = useState<string[]>([]);
	const teams = (event.teams ?? []) as KidsLeagueTeam[];
	const freeAgents = (event.freeAgents ?? []) as KidsLeagueFreeAgent[];
	const ageLimitLabel = getAgeLimitLabel(event);
	const seasonBadge = getSeasonBadgeLabel(event.season);
	const seasonBadgeClass = getSeasonBadgeClass(event.season);
	const candidateAthletes = athletes.filter(
		(athlete) => event.season === "Either" || event.season === athlete.season,
	);
	const availableAthletes = candidateAthletes.filter(
		(athlete) =>
			getAthleteEventAgeLimitReason(athlete, event) === "" &&
			!getKidsLeagueAthleteTeam(teams, athlete.id),
	);
	const selectedAthlete =
		candidateAthletes.find((athlete) => athlete.id === selectedAthleteID) ??
		availableAthletes[0] ??
		candidateAthletes[0] ??
		null;
	const selectedAthleteTeam = selectedAthlete
		? getKidsLeagueAthleteTeam(teams, selectedAthlete.id)
		: null;
	const selectedAthleteFreeAgent = selectedAthlete
		? isAthleteKidsFreeAgent(freeAgents, selectedAthlete.id)
		: false;
	const selectedAthleteAgeLimitReason = selectedAthlete
		? getAthleteEventAgeLimitReason(selectedAthlete, event)
		: "";
	const athleteOptions = candidateAthletes.map((athlete) => {
		const ageLimitReason = getAthleteEventAgeLimitReason(athlete, event);
		const existingTeam = getKidsLeagueAthleteTeam(teams, athlete.id);
		return {
			value: athlete.id,
			label: `${athlete.firstName} ${athlete.lastName}${
				existingTeam
					? ` - on ${existingTeam.name}`
					: ageLimitReason
						? ` - ${ageLimitReason}`
						: ""
			}`,
			disabled: Boolean(existingTeam) || ageLimitReason !== "",
		};
	});
	const invitedTeam =
		inviteTeamID != null
			? teams.find((team) => team.inviteID === inviteTeamID) ?? null
			: null;
	const maxTeams = Number(event.maxTeams ?? event.maximumRegistrations);
	const hasTeamCap = Number.isFinite(maxTeams) && maxTeams > 0;
	const teamCapReached = hasTeamCap && teams.length >= maxTeams;
	const createTeamDisabled =
		!event.open ||
		!selectedAthlete ||
		Boolean(selectedAthleteTeam) ||
		selectedAthleteAgeLimitReason !== "" ||
		teamCapReached ||
		savingTeam;
	const joinTeamDisabled =
		!event.open ||
		!selectedAthlete ||
		Boolean(selectedAthleteTeam) ||
		selectedAthleteAgeLimitReason !== "" ||
		!invitedTeam ||
		savingTeam;
	const freeAgentActionDisabled =
		!event.open ||
		!selectedAthlete ||
		Boolean(selectedAthleteTeam) ||
		selectedAthleteAgeLimitReason !== "" ||
		savingTeam;
	const isFamilyOnTeam = (team: KidsLeagueTeam) =>
		(team.roster ?? []).some((member) => member.parentID === user.id);
	const getKidsPlayersNeeded = (team: KidsLeagueTeam) => {
		const minimumTeamPlayers = Number(event.minimumTeamPlayers) || 0;
		return minimumTeamPlayers > 0
			? Math.max(minimumTeamPlayers - (team.roster?.length ?? 0), 0)
			: 0;
	};
	const getAvailableFreeAgentsForTeam = (team: KidsLeagueTeam) =>
		freeAgents.filter(
			(member) =>
				!(team.roster ?? []).some(
					(rosterMember) => rosterMember.athleteID === member.athleteID,
				),
		);

	useEffect(() => {
		if (
			inviteAthleteID &&
			candidateAthletes.some((athlete) => athlete.id === inviteAthleteID)
		) {
			setSelectedAthleteID(inviteAthleteID);
			return;
		}
		if (!selectedAthleteID && availableAthletes[0]) {
			setSelectedAthleteID(availableAthletes[0].id);
		}
	}, [availableAthletes, candidateAthletes, inviteAthleteID, selectedAthleteID]);

	const buildInviteLink = (team: KidsLeagueTeam) => {
		const url = new URL(window.location.href);
		url.pathname = "/";
		url.search = "";
		url.searchParams.set("league", event.id);
		url.searchParams.set("team", team.inviteID);
		return url.toString();
	};

	const copyInviteLink = async (team: KidsLeagueTeam) => {
		setActionError("");
		setCopiedTeamID("");
		try {
			await copyTextToClipboard(buildInviteLink(team));
			setCopiedTeamID(getKidsTeamID(team));
		} catch {
			setActionError("Could not copy invite link. Try again.");
		}
	};

	const syncKidsLeagueActionResponse = (response: KidsLeagueActionResponse) => {
		if (response.event) {
			onEventChange({
				...event,
				freeAgents: response.event.freeAgents ?? event.freeAgents,
				teams: response.event.teams ?? event.teams,
			});
		}
		if (response.registration?.removed) {
			removeEventRegistration(
				response.registration.athleteID,
				response.registration.regID,
			);
		} else if (response.registration) {
			addEventRegistration(
				response.registration.eventID,
				response.registration.athleteID,
				response.registration.regID,
				response.registration.status ?? "unpaid",
				null,
				null,
			);
		}
	};

	const getPaymentFollowUpMessage = (athleteName: string) =>
		event.cost === 0 || event.cost === "0"
			? ""
			: ` ${athleteName}'s registration was created, but payment is still required to hold the spot.`;

	const getAthleteName = (athleteID: string) => {
		const athlete = athletes.find((thisAthlete) => thisAthlete.id === athleteID);
		return [athlete?.firstName, athlete?.lastName].filter(Boolean).join(" ") || "Player";
	};

	const getFreeAgentName = (member: KidsLeagueFreeAgent) =>
		member.athleteName || getAthleteName(member.athleteID);

	const getFreeAgentRosterID = (member: KidsLeagueFreeAgent) =>
		member.regID ?? `${member.parentID}-${member.athleteID}`;

	const getKidsFreeAgentInviteKey = (
		team: KidsLeagueTeam,
		member: KidsLeagueFreeAgent,
	) => `${event.id}-${getKidsTeamID(team)}-${member.athleteID}`;

	const joinFreeAgentList = async () => {
		setActionError("");
		setActionSuccess("");
		if (!selectedAthlete) {
			setActionError("Select an athlete first.");
			return;
		}
		if (selectedAthleteTeam) {
			setActionError(`${selectedAthlete.firstName} is already on ${selectedAthleteTeam.name}.`);
			return;
		}
		if (selectedAthleteAgeLimitReason) {
			setActionError(`This athlete is ${selectedAthleteAgeLimitReason} for this league.`);
			return;
		}

		setSavingTeam(true);
		try {
			const response = (await joinKidsLeagueFreeAgentList({
				athleteID: selectedAthlete.id,
				eventID: event.id,
			})) as KidsLeagueActionResponse;
			syncKidsLeagueActionResponse(response);
			setActionSuccess(
				`${selectedAthlete.firstName} joined the free-agent list.${getPaymentFollowUpMessage(
					selectedAthlete.firstName ?? "This athlete",
				)}`,
			);
		} catch (error: any) {
			setActionError(error.message ?? "Could not join the free-agent list.");
		} finally {
			setSavingTeam(false);
		}
	};

	const leaveFreeAgentList = async () => {
		setActionError("");
		setActionSuccess("");
		if (!selectedAthlete) {
			setActionError("Select an athlete first.");
			return;
		}

		setSavingTeam(true);
		try {
			const response = (await leaveKidsLeagueFreeAgentList({
				athleteID: selectedAthlete.id,
				eventID: event.id,
			})) as KidsLeagueActionResponse;
			syncKidsLeagueActionResponse(response);
			setActionSuccess(`${selectedAthlete.firstName} left the free-agent list.`);
		} catch (error: any) {
			setActionError(error.message ?? "Could not leave the free-agent list.");
		} finally {
			setSavingTeam(false);
		}
	};

	const inviteFreeAgentToTeam = async (
		team: KidsLeagueTeam,
		member: KidsLeagueFreeAgent,
	) => {
		const inviteKey = getKidsFreeAgentInviteKey(team, member);
		setActionError("");
		setActionSuccess("");
		setSendingInviteKey(inviteKey);
		try {
			await sendKidsLeagueFreeAgentInvite({
				eventID: event.id,
				freeAgentAthleteID: member.athleteID,
				teamID: getKidsTeamID(team),
			});
			setSentInviteKeys((currentKeys) =>
				currentKeys.includes(inviteKey) ? currentKeys : [...currentKeys, inviteKey],
			);
			setActionSuccess(`Invite sent to ${getFreeAgentName(member)}'s family.`);
			const nextTeams = teams.map((thisTeam) => {
				if (getKidsTeamID(thisTeam) !== getKidsTeamID(team)) {
					return thisTeam;
				}
				const existingInvites = thisTeam.freeAgentInvites ?? [];
				return {
					...thisTeam,
					freeAgentInvites: existingInvites.some(
						(invite) => invite.athleteID === member.athleteID,
					)
						? existingInvites
						: [
								...existingInvites,
								{
									athleteID: member.athleteID,
									invitedAt: new Date(),
									invitedBy: user.id,
								},
							],
				};
			});
			onEventChange({ ...event, teams: nextTeams });
		} catch (error: any) {
			setActionError(
				error?.message ||
					error?.details ||
					"Could not send the free-agent invite. Try again later.",
			);
		} finally {
			setSendingInviteKey("");
		}
	};

	const createTeam = async () => {
		const normalizedTeamName = teamName.trim();
		setActionError("");
		setActionSuccess("");
		if (!selectedAthlete) {
			setActionError("Select an athlete first.");
			return;
		}
		if (!normalizedTeamName) {
			setActionError("Enter a team name.");
			return;
		}
		if (selectedAthleteTeam) {
			setActionError(`${selectedAthlete.firstName} is already on ${selectedAthleteTeam.name}.`);
			return;
		}
		if (selectedAthleteAgeLimitReason) {
			setActionError(`This athlete is ${selectedAthleteAgeLimitReason} for this league.`);
			return;
		}
		if (teamCapReached) {
			setActionError(`This league is capped at ${maxTeams} teams.`);
			return;
		}

		setSavingTeam(true);
		try {
			const response = (await createKidsLeagueTeam({
				athleteID: selectedAthlete.id,
				eventID: event.id,
				teamName: normalizedTeamName,
			})) as KidsLeagueActionResponse;
			syncKidsLeagueActionResponse(response);
			setTeamName("");
			setActionSuccess(
				`${normalizedTeamName} created.${getPaymentFollowUpMessage(
					selectedAthlete.firstName ?? "This athlete",
				)}`,
			);
		} catch (error: any) {
			setActionError(error.message ?? "Could not create team. Try again.");
		} finally {
			setSavingTeam(false);
		}
	};

	const joinTeam = async () => {
		setActionError("");
		setActionSuccess("");
		if (!selectedAthlete) {
			setActionError("Select an athlete first.");
			return;
		}
		if (!invitedTeam) {
			setActionError("Invite link not found or team is no longer available.");
			return;
		}
		if (selectedAthleteTeam) {
			setActionError(`${selectedAthlete.firstName} is already on ${selectedAthleteTeam.name}.`);
			return;
		}
		if (selectedAthleteAgeLimitReason) {
			setActionError(`This athlete is ${selectedAthleteAgeLimitReason} for this league.`);
			return;
		}

		setSavingTeam(true);
		try {
			const response = (await joinKidsLeagueTeam({
				athleteID: selectedAthlete.id,
				eventID: event.id,
				teamInviteID: invitedTeam.inviteID,
			})) as KidsLeagueActionResponse;
			syncKidsLeagueActionResponse(response);
			clearInviteParams();
			setActionSuccess(
				`${selectedAthlete.firstName} joined ${
					response.teamName ?? invitedTeam.name
				}.${getPaymentFollowUpMessage(
					selectedAthlete.firstName ?? "This athlete",
				)}`,
			);
		} catch (error: any) {
			setActionError(error.message ?? "Could not join team. Try again.");
		} finally {
			setSavingTeam(false);
		}
	};

	return (
		<div
			className={`grid gap-3 rounded-lg border p-3 ${
				event.open
					? "border-white/10 bg-white/[0.03]"
					: "border-white/10 bg-black/20 opacity-80"
			}`}
		>
			<div className="grid gap-3">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="min-w-0">
						<div className="flex flex-wrap items-center gap-2">
							<h3 className="m-0 text-base text-white">{event.title}</h3>
							{!event.open ? (
								<span className="rounded-full border border-white/10 bg-white/10 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-300">
									Not Open
								</span>
							) : null}
						</div>
						<p className="m-0 mt-1 text-sm text-slate-400">
							{getEventScheduleLabel(event)} · {event.location} · ${event.cost}
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						{seasonBadge ? (
							<span
								className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${seasonBadgeClass}`}
							>
								{seasonBadge}
							</span>
						) : null}
						{ageLimitLabel ? (
							<span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-300">
								{ageLimitLabel}
							</span>
						) : null}
						<button
							className="rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm font-bold text-white transition hover:border-accent-color-2 hover:text-accent-color-2"
							type="button"
							onClick={() => setExpanded((current) => !current)}
						>
							{expanded ? "Less" : "Details"}
						</button>
					</div>
				</div>
			</div>

			{expanded ? (
				<div className="grid gap-3 border-t border-white/10 pt-3">
					<EventDescription description={event.description} />

					{actionError ? (
						<div className="rounded-md border border-red-300/30 bg-red-500/15 p-3 text-sm text-red-100">
							{actionError}
						</div>
					) : null}
					{actionSuccess ? (
						<div className="rounded-md border border-accent-color-2/30 bg-[rgba(108,207,246,0.12)] p-3 text-sm text-accent-color-2">
							{actionSuccess}
						</div>
					) : null}

					<div className="grid gap-2">
						<div>
							<h4 className="m-0 text-sm font-semibold uppercase tracking-[0.12em] text-slate-400">
								Teams
							</h4>
							<p className="m-0 mt-1 text-xs text-slate-500">
								You need an invite link from an existing player to join a team.
							</p>
						</div>
						{teams.length === 0 ? (
							<p className="m-0 rounded-md border border-white/10 bg-black/20 p-3 text-sm text-slate-400">
								No teams have been created yet.
							</p>
						) : (
								<div className="divide-y divide-white/10 rounded-md border border-white/10 bg-black/20">
									{teams.map((team) => {
										const playersNeeded = getKidsPlayersNeeded(team);
										const familyOnTeam = isFamilyOnTeam(team);
										const availableFreeAgents = getAvailableFreeAgentsForTeam(team);
										const invitedAthleteIDs = new Set(
											(team.freeAgentInvites ?? []).map(
												(invite) => invite.athleteID,
											),
										);

										return (
											<div key={getKidsTeamID(team)} className="grid gap-2 p-3">
												<div className="flex flex-wrap items-center justify-between gap-3">
													<div>
														<strong className="block text-sm text-white">
															{team.name}
														</strong>
														<small className="text-slate-400">
															{team.roster?.length ?? 0}{" "}
															{(team.roster?.length ?? 0) === 1
																? "player"
																: "players"}
														</small>
													</div>
													{familyOnTeam ? (
														<button
															className={inviteLinkButtonClass}
															type="button"
															onClick={() => copyInviteLink(team)}
														>
															{copiedTeamID === getKidsTeamID(team)
																? "Copied"
																: "Copy invite link"}
														</button>
													) : null}
												</div>
												{familyOnTeam &&
												playersNeeded > 0 &&
												availableFreeAgents.length > 0 ? (
													<div className="grid gap-1 border-t border-white/10 pt-2">
														<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
															Invite free agents
														</small>
														{availableFreeAgents.map((member) => {
															const inviteKey = getKidsFreeAgentInviteKey(team, member);
															const alreadyInvited =
																invitedAthleteIDs.has(member.athleteID) ||
																sentInviteKeys.includes(inviteKey);
															const sending = sendingInviteKey === inviteKey;
															return (
																<div
																	key={getFreeAgentRosterID(member)}
																	className="flex flex-wrap items-center justify-between gap-2 py-1"
																>
																	<span className="text-sm text-slate-300">
																		{getFreeAgentName(member)}
																	</span>
																	<button
																		className={authClasses.buttonSmall}
																		disabled={alreadyInvited || sending}
																		type="button"
																		onClick={() => inviteFreeAgentToTeam(team, member)}
																	>
																		{sending
																			? "Sending..."
																			: alreadyInvited
																				? "Invited"
																				: "Send Invite"}
																	</button>
																</div>
															);
														})}
													</div>
												) : null}
											</div>
										);
									})}
								</div>
							)}
						</div>

					{invitedTeam ? (
						<div className="grid gap-3 rounded-md border border-accent-color-2/30 bg-[rgba(108,207,246,0.08)] p-3">
							<div>
								<h4 className="m-0 text-base text-white">
									Join {invitedTeam.name}
								</h4>
								<p className="m-0 mt-1 text-sm text-slate-300">
									Choose which athlete should join this team.
								</p>
							</div>
							<div className="grid gap-2 md:grid-cols-[1fr_auto] md:items-end">
								<div className="grid gap-1.5">
									<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
										Athlete
									</small>
									<FormDropdown
										value={selectedAthlete?.id ?? ""}
										options={athleteOptions}
										setVal={setSelectedAthleteID}
									/>
								</div>
								<button
									className={authClasses.buttonSmall}
									disabled={joinTeamDisabled}
									type="button"
									onClick={joinTeam}
								>
									{savingTeam ? "Joining..." : "Join Team"}
								</button>
							</div>
						</div>
						) : inviteTeamID ? (
							<div className="rounded-md border border-yellow-300/30 bg-yellow-500/10 p-3 text-sm text-yellow-100">
								Invite link not found or team is no longer available.
							</div>
						) : null}

						<div className="grid gap-3 rounded-md border border-white/10 bg-black/20 p-3">
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div>
									<h4 className="m-0 text-base text-white">
										Free-Agent List
									</h4>
									<p className="m-0 mt-1 text-sm text-slate-400">
										Join this list if your athlete needs a team.
									</p>
								</div>
								<span className="rounded-full border border-white/10 bg-white/10 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-300">
									{freeAgents.length} listed
								</span>
							</div>
							{freeAgents.length > 0 ? (
								<div className="divide-y divide-white/10 rounded-md border border-white/10 bg-[#111318]">
									{freeAgents.map((member) => (
										<div
											key={getFreeAgentRosterID(member)}
											className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
										>
											<span className="text-sm font-semibold text-slate-200">
												{getFreeAgentName(member)}
											</span>
											{member.parentID === user.id ? (
												<span className="rounded-full border border-accent-color-2/30 bg-[rgba(108,207,246,0.12)] px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-accent-color-2">
													Your athlete
												</span>
											) : null}
										</div>
									))}
								</div>
							) : (
								<p className="m-0 rounded-md border border-white/10 bg-[#111318] p-3 text-sm text-slate-400">
									No free agents are listed yet.
								</p>
							)}
							<div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
								<div className="grid gap-1.5">
									<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
										Athlete
									</small>
									<FormDropdown
										value={selectedAthlete?.id ?? ""}
										options={athleteOptions}
										setVal={setSelectedAthleteID}
									/>
								</div>
								<button
									className={
										selectedAthleteFreeAgent
											? `${authClasses.buttonSmall} border-white/10 bg-[#333333] hover:bg-[#3f3f3f]`
											: authClasses.buttonSmall
									}
									disabled={freeAgentActionDisabled}
									type="button"
									onClick={
										selectedAthleteFreeAgent
											? leaveFreeAgentList
											: joinFreeAgentList
									}
								>
									{savingTeam
										? "Saving..."
										: selectedAthleteFreeAgent
											? "Leave Free-Agent List"
											: "Join Free-Agent List"}
								</button>
							</div>
							{selectedAthleteTeam ? (
								<small className="text-yellow-100">
									{selectedAthlete?.firstName} is already on{" "}
									{selectedAthleteTeam.name}.
								</small>
							) : selectedAthleteAgeLimitReason ? (
								<small className="text-yellow-100">
									This athlete is {selectedAthleteAgeLimitReason} for this league.
								</small>
							) : null}
						</div>

						<div className="grid gap-3 rounded-md border border-white/10 bg-black/20 p-3">
							<div>
								<h4 className="m-0 text-base text-white">Create Team</h4>
							<p className="m-0 mt-1 text-sm text-slate-400">
								Create a team first if your teammate has not already made one.
							</p>
						</div>
						<div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
							<div className="grid gap-1.5">
								<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
									Athlete
								</small>
								<FormDropdown
									value={selectedAthlete?.id ?? ""}
									options={athleteOptions}
									setVal={setSelectedAthleteID}
								/>
							</div>
							<label className="grid gap-1.5">
								<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
									Team Name
								</small>
								<input
									className="rounded-md border border-white/10 bg-[#111318] px-3 py-2 text-sm text-white outline-none transition focus:border-accent-color-2"
									value={teamName}
									onChange={(eventChange) => setTeamName(eventChange.target.value)}
									placeholder="Team name"
								/>
							</label>
							<button
								className={authClasses.buttonSmall}
								disabled={createTeamDisabled}
								type="button"
								onClick={createTeam}
							>
								{savingTeam ? "Creating..." : "Create Team"}
							</button>
						</div>
						{selectedAthleteTeam ? (
							<small className="text-yellow-100">
								{selectedAthlete?.firstName} is already on {selectedAthleteTeam.name}.
							</small>
						) : selectedAthleteAgeLimitReason ? (
							<small className="text-yellow-100">
								This athlete is {selectedAthleteAgeLimitReason} for this league.
							</small>
						) : teamCapReached ? (
							<small className="text-yellow-100">
								This league is capped at {maxTeams} teams.
							</small>
						) : null}
					</div>
				</div>
			) : null}
		</div>
	);
};

type AdultEventCardProps = {
	adultProfileSaved: boolean;
	event: DocumentData;
	inviteTeamID: string | null;
	onEventChange: (event: DocumentData) => void;
	rosterProfiles: Record<string, AdultRosterProfile>;
	user: User;
	userData: DocumentData;
};

const AdultEventCard: FC<AdultEventCardProps> = ({
	adultProfileSaved,
	event,
	inviteTeamID,
	onEventChange,
	rosterProfiles,
	user,
	userData,
}) => {
	const { buttonSmall } = authClasses;
	const [expanded, setExpanded] = useState(Boolean(inviteTeamID));
	const [teamName, setTeamName] = useState("");
	const [actionError, setActionError] = useState("");
	const [actionSuccess, setActionSuccess] = useState("");
	const [savingTeam, setSavingTeam] = useState(false);
	const [waiverSigned, setWaiverSigned] = useState(false);
	const [checkingWaiver, setCheckingWaiver] = useState(true);
	const [waiverModalOpen, setWaiverModalOpen] = useState(false);
	const [invitePromptDismissed, setInvitePromptDismissed] = useState(false);
	const [createdTeamModal, setCreatedTeamModal] =
		useState<AdultLeagueTeam | null>(null);
	const [copyingCreatedInvite, setCopyingCreatedInvite] = useState(false);
	const [copiedCreatedInvite, setCopiedCreatedInvite] = useState(false);
	const [sendingCreatedInviteKey, setSendingCreatedInviteKey] = useState("");
	const [sentCreatedInviteKeys, setSentCreatedInviteKeys] = useState<string[]>(
		[],
	);
	const [freeAgentOverride, setFreeAgentOverride] = useState<boolean | null>(
		null,
	);
	const freeAgentActionPendingRef = useRef(false);
	const pendingWaiverActionRef = useRef<(() => void) | null>(null);
	const capacity = getEventCapacity(event, null);
	const ageLimitLabel = getAgeLimitLabel(event);
	const teams = (event.teams ?? []) as AdultLeagueTeam[];
	const freeAgents = (event.freeAgents ?? []) as AdultFreeAgent[];
	const userTeam = teams.find((team) => isUserOnTeam(team, user.id));
	const userFreeAgentFromEvent = isUserFreeAgent(freeAgents, user.id);
	const userFreeAgent = freeAgentOverride ?? userFreeAgentFromEvent;
	const invitedTeam = inviteTeamID
		? teams.find((team) => getTeamInviteID(team) === inviteTeamID)
		: null;
	const showInvitePrompt =
		Boolean(inviteTeamID) && Boolean(invitedTeam) && !userTeam && !invitePromptDismissed;
	const minimumTeamPlayers = Number(event.minimumTeamPlayers) || null;
	const leagueFormatLabel = getAdultLeagueFormatLabel(event.season);
	const maxTeams = Number(event.maximumRegistrations);
	const hasTeamCap =
		event.maximumRegistrations != null &&
		event.maximumRegistrations !== "" &&
		Number.isInteger(maxTeams) &&
		maxTeams > 0;
	const teamCapReached = hasTeamCap && teams.length >= maxTeams;
	const createTeamBlockedByFreeAgent =
		userFreeAgent || (savingTeam && userFreeAgentFromEvent);
	useEffect(() => {
		setFreeAgentOverride(null);
	}, [userFreeAgentFromEvent]);
	useEffect(() => {
		let active = true;
		const loadWaiverStatus = async () => {
			setCheckingWaiver(true);
			try {
				const signed = await hasSignedAdultLeagueWaiver(user.id, [event]);
				if (active) {
					setWaiverSigned(signed);
				}
			} catch {
				if (active) {
					setWaiverSigned(false);
				}
			} finally {
				if (active) {
					setCheckingWaiver(false);
				}
			}
		};

		loadWaiverStatus();

		return () => {
			active = false;
		};
	}, [event, event.id, user.id]);
	const requireAdultWaiver = (nextAction: () => void) => {
		setActionError("");
		if (checkingWaiver) {
			setActionError("Checking waiver status. Try again in a moment.");
			return;
		}
		if (waiverSigned) {
			nextAction();
			return;
		}
		pendingWaiverActionRef.current = nextAction;
		setWaiverModalOpen(true);
	};
	const finishWaiver = () => {
		setWaiverSigned(true);
		setWaiverModalOpen(false);
		const pendingAction = pendingWaiverActionRef.current;
		pendingWaiverActionRef.current = null;
		pendingAction?.();
	};
	const getMemberData = (): AdultTeamRosterMember => ({
		userID: user.id,
		joinedAt: new Date(),
	});
	const getRosterName = (member: AdultTeamRosterMember) =>
		getRosterMemberName(member, rosterProfiles);
	const getInviteLink = (team: AdultLeagueTeam) => {
		const url = new URL(window.location.href);
		url.pathname = "/";
		url.search = "";
		url.searchParams.set("league", event.id);
		url.searchParams.set("team", getTeamInviteID(team));
		return url.toString();
	};
	const getCreatedInviteKey = (team: AdultLeagueTeam, member: AdultFreeAgent) =>
		`${event.id}-${getTeamID(team)}-${member.userID}`;
	const getAvailableFreeAgentsForTeam = (team: AdultLeagueTeam | null) =>
		freeAgents.filter(
			(member) =>
				!team ||
				!(team.roster ?? []).some((player) => player.userID === member.userID),
		);
	const copyCreatedTeamInviteLink = async (team: AdultLeagueTeam) => {
		setActionError("");
		setCopiedCreatedInvite(false);
		setCopyingCreatedInvite(true);
		try {
			await copyTextToClipboard(getInviteLink(team));
			setCopiedCreatedInvite(true);
		} catch (error: any) {
			setActionError(error.message ?? "Could not copy invite link. Try again.");
		} finally {
			setCopyingCreatedInvite(false);
		}
	};
	const inviteFreeAgentToCreatedTeam = async (
		team: AdultLeagueTeam,
		member: AdultFreeAgent,
	) => {
		const inviteKey = getCreatedInviteKey(team, member);
		setActionError("");
		setSendingCreatedInviteKey(inviteKey);
		try {
			await sendAdultFreeAgentInvite({
				eventID: event.id,
				freeAgentUserID: member.userID,
				teamID: getTeamID(team),
			});
			setSentCreatedInviteKeys((currentKeys) =>
				currentKeys.includes(inviteKey) ? currentKeys : [...currentKeys, inviteKey],
			);
			onEventChange({
				...event,
				teams: teams.map((currentTeam) => {
					if (getTeamID(currentTeam) !== getTeamID(team)) {
						return currentTeam;
					}
					const existingInvites = currentTeam.freeAgentInvites ?? [];
					return {
						...currentTeam,
						freeAgentInvites: existingInvites.some(
							(invite) => invite.userID === member.userID,
						)
							? existingInvites
							: [
									...existingInvites,
									{
										userID: member.userID,
										invitedAt: new Date(),
										invitedBy: user.id,
									},
								],
					};
				}),
			});
		} catch (error: any) {
			setActionError(
				error?.message ||
					error?.details ||
					"Could not send the free-agent invite. Try again later.",
			);
		} finally {
			setSendingCreatedInviteKey("");
		}
	};
	const renderFreeAgentPickupList = (
		team: AdultLeagueTeam | null,
		mode: "preview" | "invite",
	) => {
		const availableFreeAgents = getAvailableFreeAgentsForTeam(team);
		const invitedUserIDs = new Set(
			(team?.freeAgentInvites ?? []).map((invite) => invite.userID),
		);

		return (
			<div className="grid gap-2 rounded-md border border-white/10 bg-black/20 p-3">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<div>
						<small className={adultSectionLabelClass}>
							Available Free Agents
						</small>
						<p className="m-0 mt-1 text-sm text-slate-400">
							{mode === "invite"
								? "Send an invite link to players looking for a team."
								: "These players are looking for a team. Create a team first to invite them."}
						</p>
					</div>
					<span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-xs font-semibold text-slate-300">
						{availableFreeAgents.length}
					</span>
				</div>
				{availableFreeAgents.length > 0 ? (
					<div className="divide-y divide-white/10 border-y border-white/10">
						{availableFreeAgents.map((member) => {
							const inviteKey = team ? getCreatedInviteKey(team, member) : "";
							const alreadyInvited =
								Boolean(team) &&
								(invitedUserIDs.has(member.userID) ||
									sentCreatedInviteKeys.includes(inviteKey));
							const sending = sendingCreatedInviteKey === inviteKey;

							return (
								<div
									className="flex flex-wrap items-center justify-between gap-2 py-2"
									key={member.userID}
								>
									<span className="text-sm font-semibold text-slate-200">
										{getRosterMemberName(member, rosterProfiles)}
									</span>
									{mode === "invite" && team ? (
										<button
											className={buttonSmall}
											disabled={alreadyInvited || sending}
											type="button"
											onClick={() => inviteFreeAgentToCreatedTeam(team, member)}
										>
											{sending
												? "Sending..."
												: alreadyInvited
													? "Invited"
													: "Send Invite"}
										</button>
									) : null}
								</div>
							);
						})}
					</div>
				) : (
					<p className="m-0 text-sm text-slate-400">
						No free agents are listed for this league right now.
					</p>
				)}
			</div>
		);
	};
	const updateLeagueInState = ({
		nextFreeAgents = freeAgents,
		nextTeams = teams,
	}: {
		nextFreeAgents?: AdultFreeAgent[];
		nextTeams?: AdultLeagueTeam[];
	}) => {
		onEventChange({ ...event, freeAgents: nextFreeAgents, teams: nextTeams });
	};
	const updateTeamsInTransaction = async (
		getNextTeams: (currentTeams: AdultLeagueTeam[]) => AdultLeagueTeam[],
	) => {
		const eventRef = doc(db, "events", event.id);
		const result = await runTransaction(db, async (transaction) => {
			const eventSnap = await transaction.get(eventRef);
			if (!eventSnap.exists()) {
				throw new Error("League not found.");
			}

			const eventData = eventSnap.data();
			const currentTeams = (eventData.teams ?? []) as AdultLeagueTeam[];
			const currentFreeAgents = (eventData.freeAgents ?? []) as AdultFreeAgent[];
			const updatedTeams = getNextTeams(currentTeams);
			const updatedFreeAgents = currentFreeAgents.filter(
				(member) => member.userID !== user.id,
			);
			transaction.update(eventRef, {
				freeAgents: updatedFreeAgents,
				teams: updatedTeams,
			});
			return { updatedFreeAgents, updatedTeams };
		});
		updateLeagueInState({
			nextFreeAgents: result.updatedFreeAgents,
			nextTeams: result.updatedTeams,
		});
		return result.updatedTeams;
	};
	const updateFreeAgentsInTransaction = async (
		getNextFreeAgents: (currentFreeAgents: AdultFreeAgent[]) => AdultFreeAgent[],
	) => {
		const eventRef = doc(db, "events", event.id);
		const nextFreeAgents = await runTransaction(db, async (transaction) => {
			const eventSnap = await transaction.get(eventRef);
			if (!eventSnap.exists()) {
				throw new Error("League not found.");
			}

			const currentFreeAgents = (eventSnap.data().freeAgents ??
				[]) as AdultFreeAgent[];
			const updatedFreeAgents = getNextFreeAgents(currentFreeAgents);
			transaction.update(eventRef, { freeAgents: updatedFreeAgents });
			return updatedFreeAgents;
		});
		updateLeagueInState({ nextFreeAgents });
		return nextFreeAgents;
	};
	const createTeam = async () => {
		const normalizedTeamName = teamName.trim();
		setActionError("");
		setActionSuccess("");
		if (!normalizedTeamName) {
			setActionError("Enter a team name.");
			return;
		}
		if (userTeam) {
			setActionError(`You are already on ${userTeam.name}.`);
			return;
		}
		if (!adultProfileSaved) {
			setActionError("Complete and save your information before creating a team.");
			return;
		}
		if (userFreeAgent) {
			setActionError("Leave the free-agent list before creating a team.");
			return;
		}
		if (freeAgentActionPendingRef.current) {
			setActionError("Finish updating the free-agent list before creating a team.");
			return;
		}
		if (teamCapReached) {
			setActionError(`This league is capped at ${maxTeams} teams.`);
			return;
		}
		if (
			teams.some(
				(team) => team.name.toLowerCase() === normalizedTeamName.toLowerCase(),
			)
		) {
			setActionError("A team with that name already exists.");
			return;
		}

		setSavingTeam(true);
		try {
			const createdTeam: AdultLeagueTeam = {
				id: createTeamID(),
				name: normalizedTeamName,
				inviteID: createTeamInviteID(normalizedTeamName),
				roster: [{ ...getMemberData(), isCaptain: true }],
				createdBy: user.id,
				createdAt: new Date(),
			};
			await updateTeamsInTransaction((currentTeams) => {
				if (currentTeams.some((team) => isUserOnTeam(team, user.id))) {
					throw new Error("You are already on a team in this league.");
				}
				if (hasTeamCap && currentTeams.length >= maxTeams) {
					throw new Error(`This league is capped at ${maxTeams} teams.`);
				}
				if (
					currentTeams.some(
						(team) =>
							team.name.toLowerCase() === normalizedTeamName.toLowerCase(),
					)
				) {
					throw new Error("A team with that name already exists.");
				}

				return [
					...currentTeams,
					createdTeam,
				];
			});
			setTeamName("");
			setCreatedTeamModal(createdTeam);
			setCopiedCreatedInvite(false);
			setActionSuccess(
				`Team ${normalizedTeamName} created. Next, copy the invite link and send it to your teammates.`,
			);
		} catch (error: any) {
			setActionError(
				error.message ?? "Could not create team. Try again or contact Sidewinder.",
			);
		} finally {
			setSavingTeam(false);
		}
	};
	const joinTeam = async (teamIDToJoin: string) => {
		setActionError("");
		setActionSuccess("");
		if (userTeam) {
			setActionError(`You are already on ${userTeam.name}.`);
			return;
		}
		if (!adultProfileSaved) {
			setActionError("Complete and save your information before joining this team.");
			return;
		}
		setSavingTeam(true);
		try {
			let joinedTeamName = "this team";
			await updateTeamsInTransaction((currentTeams) => {
				if (currentTeams.some((team) => isUserOnTeam(team, user.id))) {
					throw new Error("You are already on a team in this league.");
				}
				const teamToJoin = currentTeams.find(
					(team) => getTeamID(team) === teamIDToJoin,
				);
				if (!teamToJoin) {
					throw new Error("Invite link not found or team is no longer available.");
				}
				joinedTeamName = teamToJoin.name;

				return currentTeams.map((team) =>
					getTeamID(team) === teamIDToJoin
						? {
								...team,
								roster: [...(team.roster ?? []), getMemberData()],
							}
						: team,
				);
			});
			setInvitePromptDismissed(true);
			clearInviteParams();
			setActionSuccess(`You joined ${joinedTeamName}.`);
		} catch (error: any) {
			setActionError(
				error.message ?? "Could not join team. Try again or contact Sidewinder.",
			);
		} finally {
			setSavingTeam(false);
		}
	};
	const joinFreeAgentList = async () => {
		if (freeAgentActionPendingRef.current) {
			return;
		}

		setActionError("");
		setActionSuccess("");
		if (userTeam) {
			setActionError(`You are already on ${userTeam.name}.`);
			return;
		}
		if (!adultProfileSaved) {
			setActionError(
				"Complete and save your information before joining the free-agent list.",
			);
			return;
		}

		freeAgentActionPendingRef.current = true;
		setFreeAgentOverride(true);
		setSavingTeam(true);
		try {
			await updateFreeAgentsInTransaction((currentFreeAgents) => {
				if (isUserFreeAgent(currentFreeAgents, user.id)) {
					return currentFreeAgents;
				}

				return [...currentFreeAgents, { userID: user.id, joinedAt: new Date() }];
			});
			setActionSuccess("You joined the free-agent list.");
		} catch (error: any) {
			setFreeAgentOverride(null);
			setActionError(
				error.message ??
					"Could not join the free-agent list. Try again or contact Sidewinder.",
			);
		} finally {
			freeAgentActionPendingRef.current = false;
			setSavingTeam(false);
		}
	};
	const leaveFreeAgentList = async () => {
		if (freeAgentActionPendingRef.current) {
			return;
		}

		setActionError("");
		setActionSuccess("");
		freeAgentActionPendingRef.current = true;
		setFreeAgentOverride(false);
		setSavingTeam(true);
		try {
			await updateFreeAgentsInTransaction((currentFreeAgents) =>
				currentFreeAgents.filter((member) => member.userID !== user.id),
			);
			setActionSuccess("You left the free-agent list.");
		} catch (error: any) {
			setFreeAgentOverride(null);
			setActionError(
				error.message ??
					"Could not leave the free-agent list. Try again or contact Sidewinder.",
			);
		} finally {
			freeAgentActionPendingRef.current = false;
			setSavingTeam(false);
		}
	};
	const renderCreatedTeamModal = () =>
		createdTeamModal ? (
			<div className="fixed inset-0 z-20 flex items-center justify-center overflow-y-auto bg-black/75 px-4 py-6">
				<div className="grid max-h-[92vh] w-full max-w-2xl gap-4 overflow-y-auto rounded-lg border border-white/10 bg-[#24262c] p-5 shadow-2xl shadow-black/40">
					<div>
						<span className="text-xs font-semibold uppercase tracking-[0.14em] text-accent-color-2">
							Team Created
						</span>
						<h3 className="m-0 mt-1 text-2xl text-white">
							Invite players to {createdTeamModal.name}
						</h3>
						<p className="m-0 mt-2 text-sm text-slate-300">
							Copy your team link for friends, or invite free agents who are
							looking for a team in this league.
						</p>
					</div>

					<div className="grid gap-2 rounded-md border border-white/10 bg-black/20 p-3">
						<small className={adultSectionLabelClass}>Team Invite Link</small>
						<div className="flex flex-wrap items-center gap-3">
							<button
								className={buttonSmall}
								disabled={copyingCreatedInvite}
								type="button"
								onClick={() => copyCreatedTeamInviteLink(createdTeamModal)}
							>
								{copyingCreatedInvite ? "Copying..." : "Copy Invite Link"}
							</button>
							{copiedCreatedInvite ? (
								<small className="text-accent-color-2">Copied</small>
							) : null}
						</div>
					</div>

					{renderFreeAgentPickupList(createdTeamModal, "invite")}

					{actionError ? (
						<small className="text-yellow-100">{actionError}</small>
					) : null}

					<div className="flex justify-end border-t border-white/10 pt-4">
						<button
							className={`${buttonSmall} border-white/10 bg-[#333333] hover:bg-[#3f3f3f]`}
							type="button"
							onClick={() => setCreatedTeamModal(null)}
						>
							Done
						</button>
					</div>
				</div>
			</div>
		) : null;
	if (userTeam) {
		return (
			<>
				{renderCreatedTeamModal()}
				<div
					className={`grid gap-3 rounded-lg border p-3 ${
						event.open
							? "border-white/10 bg-white/[0.03]"
							: "border-white/10 bg-black/20 opacity-80"
					}`}
				>
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="min-w-0">
						<div className="flex flex-wrap items-center gap-2">
							<h3 className="m-0 text-base text-white">{event.title}</h3>
							<span className="rounded-full border border-accent-color-2/30 bg-[rgba(108,207,246,0.12)] px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-accent-color-2">
								Registered
							</span>
						</div>
						{actionError ? (
							<small className="mt-1 block text-yellow-100">
								{actionError}
							</small>
						) : null}
						{actionSuccess ? (
							<small className="mt-1 block text-accent-color-2">
								{actionSuccess}
							</small>
						) : null}
					</div>
					<button
						className="rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm font-bold text-white transition hover:border-accent-color-2 hover:text-accent-color-2"
						type="button"
						onClick={() => setExpanded((current) => !current)}
					>
						{expanded ? "Less" : "Details"}
					</button>
				</div>
				{expanded ? (
					<div className="grid gap-3 border-t border-white/10 pt-3">
						<div className="grid gap-2">
							<div>
								<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
									League Details
								</small>
								<div className="mt-1">
									<EventDescription
										description={event.description}
										fallback="No additional details are available yet."
									/>
								</div>
							</div>
						</div>
					</div>
				) : null}
				</div>
			</>
		);
	}

	return (
		<div
			className={`grid gap-3 rounded-lg border p-3 ${
				event.open
					? "border-white/10 bg-white/[0.03]"
					: "border-white/10 bg-black/20 opacity-80"
			}`}
		>
			{waiverModalOpen ? (
				<AdultLeagueWaiverModal
					event={event}
					onCancel={() => {
						pendingWaiverActionRef.current = null;
						setWaiverModalOpen(false);
					}}
					onSigned={finishWaiver}
					user={user}
					userData={userData}
				/>
			) : null}
			{showInvitePrompt && invitedTeam ? (
				<div className="fixed inset-0 z-20 flex items-center justify-center overflow-y-auto bg-black/75 px-4 py-6">
					<div className="grid max-h-[92vh] w-full max-w-2xl gap-4 overflow-y-auto rounded-lg border border-white/10 bg-[#24262c] p-5 shadow-2xl shadow-black/40">
						<div>
							<span className="text-xs font-semibold uppercase tracking-[0.14em] text-accent-color-2">
								Team Invite
							</span>
							<h3 className="m-0 mt-1 text-2xl text-white">
								Join {invitedTeam.name}
							</h3>
							<p className="m-0 mt-2 text-sm text-slate-300">
								You were invited to join {invitedTeam.name} for {event.title}.
							</p>
							<AdultLeagueFacts
								event={event}
								teamName={invitedTeam.name}
								status={isTeamPaid(invitedTeam) ? "Paid" : "Unpaid"}
							/>
						</div>

						{actionError ? (
							<small className="text-yellow-100">{actionError}</small>
						) : null}
						{!adultProfileSaved ? (
							<small className="text-yellow-100">
								Complete and save your information before joining this team.
							</small>
						) : null}

						<div className="flex flex-wrap justify-end gap-3 border-t border-white/10 pt-4">
							<button
								className={`${buttonSmall} border-white/10 bg-[#333333] hover:bg-[#3f3f3f]`}
								disabled={savingTeam}
								type="button"
								onClick={() => setInvitePromptDismissed(true)}
							>
								Not Now
							</button>
							<button
								className={`${buttonSmall} ${
									!adultProfileSaved ? "cursor-not-allowed opacity-50" : ""
								}`}
								disabled={savingTeam || !adultProfileSaved}
								type="button"
								onClick={() =>
									requireAdultWaiver(() => joinTeam(getTeamID(invitedTeam)))
								}
							>
								{savingTeam ? "Joining..." : "Join Team"}
							</button>
						</div>
					</div>
				</div>
			) : null}
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<h3 className="m-0 text-base text-white">{event.title}</h3>
						{!event.open ? (
							<span className="rounded-full border border-white/10 bg-white/10 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-slate-300">
								Not Open
							</span>
						) : null}
						{capacity.isSoldOut ? (
							<span className="rounded-full border border-red-300/30 bg-red-500/15 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-red-100">
								Sold Out
							</span>
						) : null}
					</div>
					<AdultLeagueFacts event={event} fee={`$${event.cost} team fee`} />
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{ageLimitLabel ? (
						<span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-300">
							{ageLimitLabel}
						</span>
					) : null}
					<span className="rounded-full border border-accent-color-2/30 bg-[rgba(108,207,246,0.12)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-accent-color-2">
						{leagueFormatLabel}
					</span>
					<button
						className="rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm font-bold text-white transition hover:border-accent-color-2 hover:text-accent-color-2"
						type="button"
						onClick={() => setExpanded((current) => !current)}
					>
						{expanded ? "Less" : "Details"}
					</button>
				</div>
			</div>

				{expanded ? (
					<div className="grid gap-3 border-t border-white/10 pt-3">
					<EventDescription description={event.description} />
					{inviteTeamID && !invitedTeam ? (
						<div className="rounded-md border border-yellow-300/30 bg-yellow-500/10 p-3 text-sm text-yellow-100">
							Invite link not found or team is no longer available.
						</div>
					) : null}
						<div className="flex flex-wrap gap-2 rounded-md border border-white/10 bg-black/20 p-3 text-sm text-slate-300">
							{minimumTeamPlayers ? (
								<span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-semibold text-slate-200">
									Minimum {minimumTeamPlayers} players
								</span>
							) : null}
							<span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-semibold text-slate-200">
								{leagueFormatLabel}
							</span>
						</div>
					<div className="grid gap-3">
							{invitedTeam ? (
								<div className="grid gap-2">
									<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
										Team Invite
									</small>
									<div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-white/10 bg-black/20 p-3">
										<div>
											<strong className="block text-white">
												{invitedTeam.name}
											</strong>
											<span className="text-sm text-slate-400">
												{isTeamPaid(invitedTeam) ? "Paid" : "Unpaid"}
											</span>
											{(invitedTeam.roster?.length ?? 0) > 0 ? (
												<span className="mt-1 block text-xs text-slate-500">
													{(invitedTeam.roster ?? [])
														.map(getRosterName)
														.join(", ")}
												</span>
											) : null}
										</div>
										<button
											className={`${buttonSmall} ${
												!adultProfileSaved ? "cursor-not-allowed opacity-50" : ""
											}`}
											disabled={savingTeam || !adultProfileSaved}
											type="button"
											onClick={() =>
												requireAdultWaiver(() =>
													joinTeam(getTeamID(invitedTeam)),
												)
											}
										>
											Join Team
										</button>
									</div>
									{!adultProfileSaved ? (
										<small className="text-yellow-100">
											Complete and save your information before joining this team.
										</small>
									) : null}
									{actionError ? (
										<small className="text-yellow-100">{actionError}</small>
									) : null}
								</div>
							) : (
								<div className="grid gap-2">
										<div>
											<small className={adultSectionLabelClass}>
												Teams
											</small>
										<p className="m-0 mt-1 text-sm text-slate-400">
											Teams are invite-only. To join an existing team, ask a
											player on that team to send you their invite link.
										</p>
									</div>
										{teams.length > 0 ? (
											teams.map((team) => {
												const paid = isTeamPaid(team);
												const rosterNames = (team.roster ?? [])
													.map(getRosterName)
													.join(", ");

													return (
														<div
															className="flex items-start gap-3 rounded-md border border-white/10 bg-black/20 px-3 py-2"
															key={getTeamID(team)}
														>
															<span
																className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border text-xs font-semibold ${
																		paid
																			? "border-green-300/30 bg-green-500/15 text-green-100"
																			: "border-red-300/30 bg-red-500/15 text-red-100"
																	}`}
																aria-label={paid ? "Team paid" : "Team unpaid"}
															>
																$
															</span>
															<div className="min-w-0 flex-1">
																<strong className="block truncate text-sm font-semibold text-white">
																	{team.name}
																</strong>
																<span className="mt-0.5 block truncate text-xs leading-snug text-slate-400">
																	{rosterNames || "No players yet"}
																</span>
															</div>
														</div>
													);
											})
										) : (
										<div className="rounded-md border border-white/10 bg-black/20 p-3 text-sm text-slate-300">
											No teams have been created yet. You can create the first
											team, or wait for another player to make a team and send
											you their invite link.
										</div>
									)}
								</div>
							)}
							<div
								className={`grid gap-2 rounded-md border p-3 ${
									userFreeAgent
										? "border-accent-color-2/40 bg-[rgba(108,207,246,0.1)]"
										: "border-white/10 bg-black/20"
								}`}
							>
								<div>
									<div className="flex flex-wrap items-center gap-2">
										<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
											Free-Agent List
										</small>
										{userFreeAgent ? (
											<span className="rounded-full border border-accent-color-2/40 bg-accent-color-2/10 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-accent-color-2">
												Currently Listed
											</span>
										) : null}
									</div>
									<p className="m-0 mt-1 text-sm text-slate-400">
										{userFreeAgent
											? "Teams can now see that you are looking for a roster spot and send you an invite link."
											: "Join this list if you are looking for a team. Existing teams can see the list and send you an invite link."}
									</p>
								</div>
								<div className="flex flex-wrap items-center gap-3">
									<button
										className={`${buttonSmall} ${
											!adultProfileSaved ? "cursor-not-allowed opacity-50" : ""
										}`}
										disabled={savingTeam || !adultProfileSaved}
										type="button"
										onClick={() =>
											userFreeAgent
												? leaveFreeAgentList()
												: requireAdultWaiver(joinFreeAgentList)
										}
									>
										{savingTeam
											? "Saving..."
											: userFreeAgent
												? "Leave Free-Agent List"
												: "Join Free-Agent List"}
									</button>
									{userFreeAgent ? (
										<small className="text-accent-color-2">
											You are listed as a free agent.
										</small>
									) : null}
								</div>
								{!adultProfileSaved ? (
									<small className="text-yellow-100">
										Complete and save your information before joining the
										free-agent list.
									</small>
								) : null}
								{actionError ? (
									<small className="text-yellow-100">{actionError}</small>
								) : null}
							</div>
							{!userFreeAgent ? renderFreeAgentPickupList(null, "preview") : null}
							{createTeamBlockedByFreeAgent ? (
								<div className="rounded-md border border-white/10 bg-black/20 p-3 text-sm text-slate-300">
									{savingTeam && userFreeAgentFromEvent
										? "Updating your free-agent status..."
										: "Leave the free-agent list before creating your own team."}
								</div>
							) : (
								<div className="grid gap-2 rounded-md border border-white/10 bg-black/20 p-3">
									<label className="grid gap-1.5">
										<small className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
											Create a Team
										</small>
										<input
											className="h-10 rounded-md border border-white/10 bg-black/30 px-3 text-sm text-white outline-none focus:border-accent-color-2"
											placeholder="Team name"
											value={teamName}
											onChange={(e) => setTeamName(e.target.value)}
										/>
									</label>
									<button
										className={`${buttonSmall} ${
											!adultProfileSaved ? "cursor-not-allowed opacity-50" : ""
										}`}
										disabled={savingTeam || teamCapReached || !adultProfileSaved}
										type="button"
										onClick={() => requireAdultWaiver(createTeam)}
									>
										{savingTeam ? "Creating..." : "Create Team"}
									</button>
									{!adultProfileSaved ? (
										<small className="text-yellow-100">
											Complete and save your information before creating a team.
										</small>
									) : null}
									{teamCapReached ? (
										<small className="text-yellow-100">
											This league has reached its team limit. You can still join
											an existing team by invite.
										</small>
									) : null}
									{actionError ? (
										<small className="text-yellow-100">{actionError}</small>
									) : null}
								</div>
							)}
					</div>
				</div>
			) : null}
		</div>
	);
};

const formatMembershipDateTime = (value: unknown) => {
	const date = parseEventDateString(value);
	if (!date) {
		return "";
	}

	return date.toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
};

type CurrentAdultMembershipCardProps = {
	copiedInviteKey: string;
	copyingInviteKey: string;
	membership: AdultLeagueMembershipSummary;
	onCopyInvite: (event: DocumentData, team: AdultLeagueTeam) => void;
	onLeave?: (event: DocumentData, team: AdultLeagueTeam) => void;
	onPay?: (event: DocumentData, team: AdultLeagueTeam) => void;
	renderFreeAgentInvitePanel: (
		event: DocumentData,
		team: AdultLeagueTeam,
		rosterProfiles: Record<string, AdultRosterProfile>,
	) => ReactNode;
};

const CurrentAdultMembershipCard: FC<CurrentAdultMembershipCardProps> = ({
	copiedInviteKey,
	copyingInviteKey,
	membership,
	onCopyInvite,
	onLeave,
	onPay,
	renderFreeAgentInvitePanel,
}) => {
	const [expanded, setExpanded] = useState(false);
	const [detailLoading, setDetailLoading] = useState(false);
	const [detailError, setDetailError] = useState("");
	const [eventDetail, setEventDetail] = useState<DocumentData | null>(null);
	const [teamDetail, setTeamDetail] = useState<AdultLeagueTeam | null>(null);
	const [detailRosterProfiles, setDetailRosterProfiles] = useState<
		Record<string, AdultRosterProfile>
	>({});
	const [profilesLoading, setProfilesLoading] = useState(false);
	const paid = Boolean(membership.paidAt);
	const nextMatchLabel = formatMembershipDateTime(membership.nextMatchAt);
	const seasonLabel = formatDateRange(membership.startDate, membership.endDate);
	const inviteKey =
		eventDetail && teamDetail ? `${eventDetail.id}-${getTeamID(teamDetail)}` : "";
	const roster = useMemo(() => teamDetail?.roster ?? [], [teamDetail?.roster]);
	const freeAgents = useMemo(
		() => ((eventDetail?.freeAgents ?? []) as AdultFreeAgent[]),
		[eventDetail?.freeAgents],
	);

	useEffect(() => {
		if (!expanded || eventDetail || !membership.eventID) {
			return;
		}

		let active = true;
		setDetailLoading(true);
		setDetailError("");
		getDoc(doc(db, "events", membership.eventID))
			.then((eventSnap) => {
				if (!eventSnap.exists()) {
					throw new Error("League not found.");
				}
				const nextEvent = {
					...eventSnap.data(),
					id: eventSnap.id,
				} as DocumentData;
				const nextTeam = ((nextEvent.teams ?? []) as AdultLeagueTeam[]).find(
					(team) =>
						getTeamID(team) === membership.teamID ||
						team.name === membership.teamName,
				);
				if (!nextTeam) {
					throw new Error("Team not found.");
				}
				if (active) {
					setEventDetail(nextEvent);
					setTeamDetail(nextTeam);
				}
			})
			.catch((error: any) => {
				if (active) {
					setDetailError(
						error.message ?? "Could not load league details. Try again.",
					);
				}
			})
			.finally(() => {
				if (active) {
					setDetailLoading(false);
				}
			});

		return () => {
			active = false;
		};
	}, [
		eventDetail,
		expanded,
		membership.eventID,
		membership.teamID,
		membership.teamName,
	]);

	useEffect(() => {
		if (!expanded || !eventDetail || !teamDetail) {
			return;
		}

		const userIDs = Array.from(
			new Set(
				[
					...roster.map((member) => member.userID),
					...freeAgents.map((member) => member.userID),
				].filter(Boolean),
			),
		);
		if (userIDs.length === 0) {
			setDetailRosterProfiles({});
			return;
		}

		let active = true;
		setProfilesLoading(true);
		getAdultRosterProfiles(userIDs)
			.then((profiles) => {
				if (active) {
					setDetailRosterProfiles(
						profiles as Record<string, AdultRosterProfile>,
					);
				}
			})
			.catch(() => {
				if (active) {
					setDetailRosterProfiles({});
				}
			})
			.finally(() => {
				if (active) {
					setProfilesLoading(false);
				}
			});

		return () => {
			active = false;
		};
	}, [eventDetail, expanded, freeAgents, roster, teamDetail]);

	const rosterNames = roster
		.map((member) => getRosterMemberName(member, detailRosterProfiles))
		.join(", ");

	return (
		<div className="rounded-md border border-white/10 bg-black/20 p-3">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<strong className="text-white">
							{membership.eventTitle ?? "Adult League"}
						</strong>
						<span
							className={`${adultStatusBadgeClass} ${
								paid
									? "border-green-300/30 bg-green-500/15 text-green-100"
									: "border-yellow-300/30 bg-yellow-500/15 text-yellow-100"
							}`}
						>
							{paid ? "Team fee paid" : "Team fee due"}
						</span>
					</div>
					<p className="m-0 mt-1 text-sm text-slate-300">
						{membership.teamName || "Free agent list"}
					</p>
					<div className={`mt-1 grid gap-0.5 ${adultMetaClass}`}>
						{seasonLabel ? <span>Season: {seasonLabel}</span> : null}
						{nextMatchLabel ? <span>Next match: {nextMatchLabel}</span> : null}
					</div>
				</div>
				<div className="flex flex-wrap justify-end gap-2">
					{!paid && eventDetail && teamDetail && onPay ? (
						<button
							className={authClasses.buttonSmall}
							onClick={() => onPay(eventDetail, teamDetail)}
						>
							Pay
						</button>
					) : null}
					<button
						className="rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm font-bold text-white transition hover:border-accent-color-2 hover:text-accent-color-2"
						type="button"
						onClick={() => setExpanded((current) => !current)}
					>
						{expanded ? "Less" : "Details"}
					</button>
				</div>
			</div>

			{expanded ? (
				<div className="mt-3 grid gap-3 border-t border-white/10 pt-3">
					{detailLoading ? (
						<p className="m-0 text-sm text-slate-400">
							Loading league details...
						</p>
					) : null}
					{detailError ? (
						<div className="rounded-md border border-yellow-300/30 bg-yellow-500/10 p-3 text-sm text-yellow-100">
							{detailError}
						</div>
					) : null}
					{eventDetail && teamDetail ? (
						<>
							<div className="grid gap-1">
								<small className={adultSectionLabelClass}>Roster</small>
								<p className="m-0 text-sm text-slate-300">
									{profilesLoading
										? "Loading roster..."
										: rosterNames || "No players listed yet"}
								</p>
								{getMinimumPlayersNeeded(eventDetail, teamDetail) > 0 ? (
									<p className="m-0 text-xs font-semibold text-yellow-100">
										{getMinimumPlayersNeeded(eventDetail, teamDetail)} more needed
									</p>
								) : null}
							</div>
							<div className="flex flex-wrap items-center gap-3">
								{!paid && onPay ? (
									<button
										className={authClasses.buttonSmall}
										onClick={() => onPay(eventDetail, teamDetail)}
									>
										Pay
									</button>
								) : null}
								<button
									className={inviteLinkButtonClass}
									disabled={copyingInviteKey === inviteKey}
									onClick={() => onCopyInvite(eventDetail, teamDetail)}
								>
									{copyingInviteKey === inviteKey
										? "Creating link..."
										: "Copy invite link"}
								</button>
								<a
									className={inviteLinkButtonClass}
									href={getLeagueUrl(eventDetail)}
									target="_blank"
									rel="noopener noreferrer"
								>
									Schedule, roster, standings
								</a>
								{copiedInviteKey === inviteKey ? (
									<small className="text-slate-300">Copied</small>
								) : null}
								{!paid && onLeave ? (
									<button
										className={`${inviteLinkButtonClass} text-red-100 hover:text-red-50`}
										onClick={() => onLeave(eventDetail, teamDetail)}
									>
										Leave team
									</button>
								) : null}
							</div>
							{renderFreeAgentInvitePanel(
								eventDetail,
								teamDetail,
								detailRosterProfiles,
							)}
						</>
					) : null}
				</div>
			) : null}
		</div>
	);
};

const YouthTeamsPage: FC<{ assignments: YouthTeamAssignment[] }> = ({
	assignments,
}) => {
	const [paymentHistoryAssignment, setPaymentHistoryAssignment] =
		useState<YouthTeamAssignment | null>(null);
	const paymentHistory = paymentHistoryAssignment?.member.payments ?? [];
	const paymentHistoryAthlete = paymentHistoryAssignment?.athlete;

	return (
			<section className="grid gap-4">
				<div>
					<p className="m-0 mt-1 text-sm text-slate-400">
						Current team assignments for your athletes.
					</p>
			</div>

			<div className="grid gap-3">
				{assignments.map(({ athlete, event, member, registration, team }) => {
					const dues = Number(team.dues) || 0;
					const discount = Number(member.discount) || 0;
					const totalPaid = getPaymentTotal(member);
					const balance = dues - discount - totalPaid;
					const balanceStatus = dues
						? getBalanceStatus(balance, totalPaid)
						: "Balance pending";
					const isPaidInFull = balanceStatus === "Paid";
					const hasDiscount = discount > 0;
					const hasPaymentHistory = (member.payments ?? []).length > 0;
					const paymentGridColumns = hasPaymentHistory
						? hasDiscount
							? "md:grid-cols-5"
							: "md:grid-cols-4"
						: hasDiscount
							? "md:grid-cols-4"
							: "md:grid-cols-3";
					const duesDisplay = !dues
						? "Pending"
						: isPaidInFull
							? "Paid"
							: formatCurrency(balance);

					return (
						<article
							className="grid gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-4"
							key={`${event.id}-${registration.regID}`}
						>
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div>
									<h3 className="m-0 text-xl text-white">{team.name}</h3>
									<p className="m-0 mt-1 text-sm text-slate-400">
										{athlete.firstName} {athlete.lastName} · {event.title}
									</p>
								</div>
								<div className="flex flex-wrap gap-2">
									<span className="rounded-full border border-accent-color-2/30 bg-[rgba(108,207,246,0.08)] px-2.5 py-1 text-xs font-bold text-accent-color-2">
										{balanceStatus}
									</span>
								</div>
							</div>

							{isPaidInFull || !dues ? (
								<div className="rounded-md border border-white/10 bg-black/20 p-3">
									<div>
										<small className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
											Dues
										</small>
										<strong
											className={
												isPaidInFull
													? "text-accent-color-2"
													: "text-yellow-100"
											}
										>
											{duesDisplay}
										</strong>
									</div>
									{hasPaymentHistory ? (
										<button
											className="mt-3 rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm font-bold text-white transition hover:border-accent-color-2/60 hover:text-accent-color-2"
											onClick={() =>
												setPaymentHistoryAssignment({
													athlete,
													event,
													member,
													registration,
													team,
												})
											}
										>
											History
										</button>
									) : null}
								</div>
							) : (
								<div
									className={`grid gap-2 rounded-md border border-white/10 bg-black/20 p-3 ${paymentGridColumns}`}
								>
									<div>
										<small className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
											Dues
										</small>
										<strong className="text-white">{formatCurrency(dues)}</strong>
									</div>
									{hasDiscount ? (
										<div>
											<small className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
												Discount
											</small>
											<strong className="text-white">
												{formatCurrency(discount)}
											</strong>
										</div>
									) : null}
									<div>
										<small className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
											Paid
										</small>
										<strong className="text-white">
											{formatCurrency(totalPaid)}
										</strong>
									</div>
									<div>
										<small className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
											Balance
										</small>
										<strong className="text-yellow-100">
											{formatCurrency(balance)}
										</strong>
									</div>
									{hasPaymentHistory ? (
										<div className="flex items-end">
											<button
												className="w-full rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm font-bold text-white transition hover:border-accent-color-2/60 hover:text-accent-color-2"
												onClick={() =>
													setPaymentHistoryAssignment({
														athlete,
														event,
														member,
														registration,
														team,
													})
												}
											>
												History
											</button>
										</div>
									) : null}
								</div>
							)}
						</article>
					);
				})}
			</div>
			{paymentHistoryAssignment ? (
				<div className="fixed inset-0 z-20 flex items-center justify-center overflow-y-auto bg-black/75 px-4 py-6">
					<div className="w-full max-w-2xl rounded-lg border border-white/10 bg-[#202126] p-5 shadow-2xl">
						<div className="flex flex-wrap items-start justify-between gap-3">
							<div>
								<h2 className="m-0 text-2xl text-white">Payment History</h2>
								<p className="m-0 mt-1 text-sm text-slate-400">
									{paymentHistoryAthlete?.firstName}{" "}
									{paymentHistoryAthlete?.lastName} ·{" "}
									{paymentHistoryAssignment.team.name}
								</p>
							</div>
							<button
								className="rounded-md border border-white/15 bg-white/10 px-3 py-2 text-sm font-bold text-white transition hover:border-accent-color-2/60 hover:text-accent-color-2"
								onClick={() => setPaymentHistoryAssignment(null)}
							>
								Close
							</button>
						</div>
						{paymentHistory.length > 0 ? (
							<div className="mt-4 overflow-x-auto">
								<table className="w-full border-collapse text-left text-sm">
									<thead>
										<tr className="border-b border-white/10 text-xs uppercase tracking-[0.12em] text-slate-500">
											<th className="py-2 pr-3">Date</th>
											<th className="py-2 pr-3">Type</th>
											<th className="py-2 pr-3 text-right">Amount</th>
										</tr>
									</thead>
									<tbody>
										{paymentHistory.map((payment, index) => (
											<tr
												className="border-b border-white/10 text-slate-200"
												key={
													payment.id ??
													`${payment.date}-${payment.amount}-${index}`
												}
											>
												<td className="py-3 pr-3">
													{formatPaymentDate(payment.date)}
												</td>
												<td className="py-3 pr-3">
													{payment.paymentType ?? payment.method ?? ""}
												</td>
												<td className="py-3 pr-3 text-right font-bold text-white">
													{formatCurrency(Number(payment.amount) || 0)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<p className="mt-4 text-sm text-slate-300">
								No payments recorded yet.
							</p>
						)}
					</div>
				</div>
			) : null}
		</section>
	);
};

const EventFirstDashboard: FC<EventFirstDashboardProps> = ({ user }) => {
	const {
		addEventRegistration,
		adultLeagueMemberships,
		athletes,
		events,
		fetchAvailableEvents,
		fetchAthletes,
		fetchData,
		getAvailableEventsErrorForType,
		isAvailableEventsLoadedForType,
		isAvailableEventsLoadingForType,
		loadError,
		loading,
		loadingStage,
		removeEventRegistration,
		updateDashboardEvent,
		userData,
	} = useDashboardData(user);
	const [registerTarget, setRegisterTarget] = useState<RegisterTarget>(null);
	const [payTarget, setPayTarget] = useState<RegisterTarget>(null);
	const [unregisterTarget, setUnregisterTarget] =
		useState<RegisterTarget>(null);
	const [copiedInviteKey, setCopiedInviteKey] = useState("");
	const [copyingInviteKey, setCopyingInviteKey] = useState("");
	const [sentFreeAgentInviteKeys, setSentFreeAgentInviteKeys] = useState<
		string[]
	>([]);
	const [openFreeAgentInvitePanelKeys, setOpenFreeAgentInvitePanelKeys] =
		useState<string[]>([]);
	const [sendingFreeAgentInviteKey, setSendingFreeAgentInviteKey] =
		useState("");
	const [teamActionError, setTeamActionError] = useState("");
	const [teamPaymentWaiverTarget, setTeamPaymentWaiverTarget] =
		useState<AdultTeamRegistration | null>(null);
	const [dashboardPage, setDashboardPage] = useState<DashboardPage | null>(null);
	const [rosterProfiles, setRosterProfiles] = useState<
		Record<string, AdultRosterProfile>
	>({});
	const adultProfileDataComplete = isAdultProfileDataComplete(userData);
	const [adultProfileSaved, setAdultProfileSaved] = useState(
		adultProfileDataComplete,
	);
	const inviteParams = new URLSearchParams(window.location.search);
	const inviteEventID = inviteParams.get("league");
	const inviteTeamID = inviteParams.get("team");
	const inviteAthleteID = inviteParams.get("athlete");
	const requestedRegistrationType = inviteParams.get("registrationType");
	const requestedEventType =
		eventSections.find(
			(section) =>
				section.value.toLowerCase() ===
				`${requestedRegistrationType ?? ""}`.toLowerCase(),
		)?.value ?? null;
	const [eventType, setEventType] = useState(
		inviteEventID && inviteTeamID
			? "League"
			: requestedEventType ?? "Tryout",
	);
	const [userSelectedEventType, setUserSelectedEventType] = useState(false);
	const [clinicFilter, setClinicFilter] = useState<ClinicFilter>("all");
	const [addingAthlete, setAddingAthlete] = useState(false);
	const activeDashboardPage = dashboardPage ?? "current";
	const visibleEvents = useMemo(() => getVisibleEvents(events), [events]);
	const youthTeamAssignments = useMemo(
		() => getYouthTeamAssignments(events, athletes),
		[athletes, events],
	);
	const rosterUserIDs = useMemo(
		() =>
			activeDashboardPage !== "registrations"
				? []
				: Array.from(
						new Set(
							visibleEvents
								.filter(isAdultLeagueEvent)
								.flatMap((event) =>
									[
										...((event.freeAgents ?? []) as AdultFreeAgent[]),
										...((event.teams ?? []) as AdultLeagueTeam[]).flatMap(
											(team) => team.roster ?? [],
										),
									]
										.map((member) => member.userID)
										.filter(Boolean),
								),
						),
					),
		[activeDashboardPage, visibleEvents],
	);
	useEffect(() => {
		setAdultProfileSaved(adultProfileDataComplete);
	}, [adultProfileDataComplete]);
	useEffect(() => {
		if (loading || dashboardPage !== null) {
			return;
		}
		setDashboardPage(inviteEventID && inviteTeamID ? "registrations" : "current");
	}, [
		dashboardPage,
		inviteEventID,
		inviteTeamID,
		loading,
	]);
	useEffect(() => {
		let active = true;
		const loadRosterProfiles = async () => {
			try {
				const profiles = (await getAdultRosterProfiles(
					rosterUserIDs,
				)) as Record<string, AdultRosterProfile>;

				if (active) {
					setRosterProfiles(profiles);
				}
			} catch {
				if (active) {
					setRosterProfiles({});
				}
			}
		};

		if (rosterUserIDs.length === 0) {
			setRosterProfiles({});
		} else {
			loadRosterProfiles();
		}

		return () => {
			active = false;
		};
	}, [rosterUserIDs]);
	const selectedEventSection =
		eventSections.find((section) => section.value === eventType) ??
		eventSections[0];
	const selectedAvailableEventsLoaded =
		isAvailableEventsLoadedForType(eventType);
	const selectedAvailableEventsLoading =
		isAvailableEventsLoadingForType(eventType);
	const selectedAvailableEventsError =
		getAvailableEventsErrorForType(eventType);
	const unpaidRegistrations = getUnpaidRegistrations(
		athletes,
		visibleEvents,
	);
	const visiblePaidRegistrations = getVisiblePaidRegistrations(
		athletes,
		visibleEvents,
	);
	const inviteLeague = inviteEventID
		? visibleEvents.find((event) => event.id === inviteEventID)
		: null;
	const inviteLeagueAny = inviteEventID
		? events.find((event) => event.id === inviteEventID)
		: null;
	const inviteLinkInvalid =
		Boolean(inviteEventID && inviteTeamID) &&
		selectedAvailableEventsLoaded &&
		(!inviteLeague ||
			!((inviteLeague.teams ?? []) as AdultLeagueTeam[]).some(
				(team) => getTeamInviteID(team) === inviteTeamID,
			));
	const inviteUnavailableMessage =
		inviteLeagueAny?.hidden === true
			? "This league is over and team invite links are no longer available."
			: "Invite link not found or team is no longer available.";
	const adultTeamMemberships = adultLeagueMemberships.filter(
		(membership) => membership.role === "player",
	);
	const unpaidAdultTeamMemberships = adultTeamMemberships.filter(
		(membership) => !membership.paidAt,
	);
	const paidAdultTeamMemberships = adultTeamMemberships.filter(
		(membership) => Boolean(membership.paidAt),
	);
	const hasTopPanel =
		unpaidRegistrations.length > 0 ||
		unpaidAdultTeamMemberships.length > 0 ||
		visiblePaidRegistrations.length > 0 ||
		paidAdultTeamMemberships.length > 0;
	const eventCanBeRegistered = (event: DocumentData) => {
		if (getEventRegistrationType(event) === "adult") {
			return true;
		}
		if (isKidsLeagueEvent(event)) {
			return athletes.some((athlete) => {
				const athleteCanRegister =
					event.season === "Either" || event.season === athlete.season;
				return (
					athleteCanRegister &&
					getAthleteEventAgeLimitReason(athlete, event) === ""
				);
			});
		}

		return athletes.some((athlete) =>
			getAvailableEvents(athlete, [event]).some((availableEvent) => {
				const athleteCanRegister =
					event.season === "Either" || event.season === athlete.season;
				return availableEvent.id === event.id && athleteCanRegister;
			}),
		);
	};
	const sortEventsByDate = (a: DocumentData, b: DocumentData) => {
		const dateA = getEventDate(a)?.getTime() ?? 0;
		const dateB = getEventDate(b)?.getTime() ?? 0;
		return dateA - dateB;
	};
	const getRegisterableEventsForType = (
		nextEventType: string,
		nextClinicFilter: ClinicFilter = clinicFilter,
	) =>
		(isAvailableEventsLoadedForType(nextEventType) ? visibleEvents : [])
			.filter((event) => event.type === nextEventType)
			.filter((event) =>
				nextEventType === "Clinic"
					? eventMatchesClinicFilter(event, nextClinicFilter)
					: true,
			)
			.filter(eventCanBeRegistered)
			.sort(sortEventsByDate);
	const availableEvents = getRegisterableEventsForType(eventType);
	const tryoutEventGroups = [
		{ title: "Tryouts", events: availableEvents.filter((event) => event.subType !== "Open House") },
		{ title: "Open Houses", events: availableEvents.filter((event) => event.subType === "Open House") },
	];
	const adultLeagueEvents = availableEvents.filter(isAdultLeagueEvent);
	const kidsLeagueEvents = availableEvents.filter(isKidsLeagueEvent);
	const inProgressAdultLeagueEvents = adultLeagueEvents.filter(isLeagueInProgress);
	const futureAdultLeagueEvents = adultLeagueEvents.filter(
		(event) => !isLeagueInProgress(event),
	);
	const inProgressKidsLeagueEvents = kidsLeagueEvents.filter(isLeagueInProgress);
	const futureKidsLeagueEvents = kidsLeagueEvents.filter(
		(event) => !isLeagueInProgress(event),
	);
	const hasKidsLeagueInventory =
		eventType === "League" &&
		(selectedAvailableEventsLoaded ? visibleEvents : []).some(isKidsLeagueEvent);
	const availableTryoutCount = getRegisterableEventsForType("Tryout").length;
	const availableClinicCount = getRegisterableEventsForType("Clinic", "all").length;
	useEffect(() => {
		if (
			activeDashboardPage !== "registrations" ||
			selectedAvailableEventsLoaded ||
			selectedAvailableEventsLoading
		) {
			return;
		}

		fetchAvailableEvents(eventType).catch(() => {
			// Keep Current usable even if the available registration inventory fails.
		});
		}, [
			activeDashboardPage,
			eventType,
			fetchAvailableEvents,
			selectedAvailableEventsLoaded,
			selectedAvailableEventsLoading,
		]);
	useEffect(() => {
		if (
			loading ||
			!selectedAvailableEventsLoaded ||
			userSelectedEventType ||
			inviteEventID ||
			inviteTeamID ||
			requestedEventType ||
			eventType !== "Tryout" ||
			availableTryoutCount > 0 ||
			availableClinicCount === 0
		) {
			return;
		}

		setEventType("Clinic");
	}, [
		availableClinicCount,
		availableTryoutCount,
		eventType,
		inviteEventID,
		inviteTeamID,
		loading,
		requestedEventType,
		selectedAvailableEventsLoaded,
		userSelectedEventType,
	]);
	useEffect(() => {
		if (
			loading ||
			userSelectedEventType ||
			inviteEventID ||
			inviteTeamID ||
			requestedEventType ||
			eventType !== "Tryout" ||
			!selectedAvailableEventsLoaded ||
			availableTryoutCount > 0 ||
			isAvailableEventsLoadedForType("Clinic") ||
			isAvailableEventsLoadingForType("Clinic")
		) {
			return;
		}

		fetchAvailableEvents("Clinic").catch(() => {
			// The selected Tryout tab remains usable if clinic fallback loading fails.
		});
		}, [
			availableTryoutCount,
			eventType,
			fetchAvailableEvents,
		inviteEventID,
			inviteTeamID,
			isAvailableEventsLoadedForType,
			isAvailableEventsLoadingForType,
			loading,
		requestedEventType,
		selectedAvailableEventsLoaded,
		userSelectedEventType,
	]);
	const updateEventInState = (event?: DocumentData) => {
		if (event) {
			updateDashboardEvent(event);
			return;
		}
		fetchData();
	};
	const renderAdultLeagueCards = (leagueEvents: DocumentData[]) =>
		leagueEvents.map((event) => (
			<AdultEventCard
				key={event.id}
				event={event}
				inviteTeamID={event.id === inviteEventID ? inviteTeamID : null}
				onEventChange={updateEventInState}
				rosterProfiles={rosterProfiles}
				user={user}
				userData={userData}
				adultProfileSaved={adultProfileSaved}
				/>
			));
	const renderKidsLeagueCards = (leagueEvents: DocumentData[]) =>
		leagueEvents.map((event) => (
			<KidsLeagueEventCard
				addEventRegistration={addEventRegistration}
				key={event.id}
				event={event}
				athletes={athletes}
				inviteAthleteID={event.id === inviteEventID ? inviteAthleteID : null}
				inviteTeamID={event.id === inviteEventID ? inviteTeamID : null}
				onEventChange={updateEventInState}
				removeEventRegistration={removeEventRegistration}
				user={user}
			/>
		));
	const getInviteLink = (event: DocumentData, team: AdultLeagueTeam) => {
		const url = new URL(window.location.href);
		url.pathname = "/";
		url.search = "";
		url.searchParams.set("league", event.id);
		url.searchParams.set("team", getTeamInviteID(team));
		return url.toString();
	};
	const copyTeamInviteLink = async (
		event: DocumentData,
		team: AdultLeagueTeam,
	) => {
		const inviteKey = `${event.id}-${getTeamID(team)}`;
		setCopiedInviteKey("");
		setTeamActionError("");
		setCopyingInviteKey(inviteKey);
		try {
			await copyTextToClipboard(getInviteLink(event, team));
			setCopiedInviteKey(inviteKey);
		} catch (error: any) {
			setTeamActionError(
				error.message ?? "Could not copy invite link. Try again.",
			);
		} finally {
			setCopyingInviteKey("");
		}
	};
	const getFreeAgentInviteKey = (
		event: DocumentData,
		team: AdultLeagueTeam,
		member: AdultFreeAgent,
	) => `${event.id}-${getTeamID(team)}-${member.userID}`;
	const inviteFreeAgentToTeam = async (
		event: DocumentData,
		team: AdultLeagueTeam,
		member: AdultFreeAgent,
	) => {
		const inviteKey = getFreeAgentInviteKey(event, team, member);
		setTeamActionError("");
		setSendingFreeAgentInviteKey(inviteKey);
		try {
			await sendAdultFreeAgentInvite({
				eventID: event.id,
				freeAgentUserID: member.userID,
				teamID: getTeamID(team),
			});
			setSentFreeAgentInviteKeys((currentKeys) =>
				currentKeys.includes(inviteKey)
					? currentKeys
					: [...currentKeys, inviteKey],
			);
		} catch (error: any) {
			setTeamActionError(
				error?.message ||
					error?.details ||
					"Could not send the free-agent invite. Try again later.",
			);
		} finally {
			setSendingFreeAgentInviteKey("");
		}
	};
	const renderFreeAgentInvitePanel = (
		event: DocumentData,
		team: AdultLeagueTeam,
		panelRosterProfiles = rosterProfiles,
	) => {
		const playersNeeded = getMinimumPlayersNeeded(event, team);
		if (playersNeeded <= 0) {
			return null;
		}

		const invitedUserIDs = new Set(
			(team.freeAgentInvites ?? []).map((invite) => invite.userID),
		);
		const availableFreeAgents = ((event.freeAgents ?? []) as AdultFreeAgent[]).filter(
			(member) =>
				!(team.roster ?? []).some((player) => player.userID === member.userID),
		);
		const panelKey = `${event.id}-${getTeamID(team)}-free-agents`;
		const panelOpen = openFreeAgentInvitePanelKeys.includes(panelKey);
		const togglePanel = () => {
			setOpenFreeAgentInvitePanelKeys((currentKeys) =>
				currentKeys.includes(panelKey)
					? currentKeys.filter((key) => key !== panelKey)
					: [...currentKeys, panelKey],
			);
		};

		return (
			<div className="mt-3 border-t border-white/10 pt-3">
				<button
					className="flex w-full flex-wrap items-center justify-between gap-2 border-0 bg-transparent p-0 text-left"
					type="button"
					onClick={togglePanel}
				>
					<span>
						<span className="block text-sm font-semibold text-white">
							Invite free agents
						</span>
						<span className="block text-xs text-slate-400">
							Needs {playersNeeded} more{" "}
							{playersNeeded === 1 ? "player" : "players"} ·{" "}
							{availableFreeAgents.length} available
						</span>
					</span>
					<span className="text-xs font-semibold uppercase tracking-[0.12em] text-accent-color-2">
						{panelOpen ? "Hide" : "Show"}
					</span>
				</button>

				{panelOpen && availableFreeAgents.length > 0 ? (
					<div className="mt-3 divide-y divide-white/10 border-y border-white/10">
						{availableFreeAgents.map((member) => {
							const inviteKey = getFreeAgentInviteKey(event, team, member);
							const alreadyInvited =
								invitedUserIDs.has(member.userID) ||
								sentFreeAgentInviteKeys.includes(inviteKey);
							const sending = sendingFreeAgentInviteKey === inviteKey;

							return (
								<div
									className="flex flex-wrap items-center justify-between gap-2 py-2"
									key={member.userID}
								>
									<span className="text-sm font-semibold text-slate-200">
										{getRosterMemberName(member, panelRosterProfiles)}
									</span>
									<button
										className={authClasses.buttonSmall}
										disabled={alreadyInvited || sending}
										type="button"
										onClick={() => inviteFreeAgentToTeam(event, team, member)}
									>
										{sending
											? "Sending..."
											: alreadyInvited
												? "Invited"
												: "Send Invite"}
									</button>
								</div>
							);
						})}
					</div>
				) : null}

				{panelOpen && availableFreeAgents.length === 0 ? (
					<p className="m-0 mt-3 text-sm text-slate-400">
						No free agents are listed for this league right now.
					</p>
				) : null}
			</div>
		);
	};
	const startTeamPayment = async (
		event: DocumentData,
		team: AdultLeagueTeam,
	) => {
		setTeamActionError("");
		const playersNeeded = getMinimumPlayersNeeded(event, team);
		if (
			playersNeeded > 0 &&
			!window.confirm(
				`This team is ${playersNeeded} ${
					playersNeeded === 1 ? "player" : "players"
				} below the listed minimum. You can still pay now and add teammates later. Continue to payment?`,
			)
		) {
			return;
		}

		try {
			let paymentRegistrationID = team.paymentRegistrationID;
			let resolvedTeamID = getTeamID(team);
			if (!paymentRegistrationID) {
				const eventRef = doc(db, "events", event.id);
				const registrationRef = doc(collection(db, "registrations"));
				paymentRegistrationID = await runTransaction(db, async (transaction) => {
					const eventSnap = await transaction.get(eventRef);
					if (!eventSnap.exists()) {
						throw new Error("League not found.");
					}

					const currentTeams = (eventSnap.data().teams ?? []) as AdultLeagueTeam[];
					let resolvedRegistrationID = "";
					const nextTeams = currentTeams.map((thisTeam) => {
						if (getTeamID(thisTeam) !== getTeamID(team)) {
							return thisTeam;
						}

						resolvedTeamID = getTeamID(thisTeam);
						if (thisTeam.paymentRegistrationID) {
							resolvedRegistrationID = thisTeam.paymentRegistrationID;
							return thisTeam;
						}

						resolvedRegistrationID = registrationRef.id;
						transaction.set(registrationRef, {
							event: event.id,
							parentID: user.id,
							payerID: user.id,
							registrationType: "adultTeam",
							isTeamPayment: true,
							teamName: team.name,
							teamID: getTeamID(thisTeam),
							status: "unpaid",
						});
						return {
							...thisTeam,
							paymentRegistrationID: resolvedRegistrationID,
						};
					});

					if (!resolvedRegistrationID) {
						throw new Error("Team not found.");
					}

					transaction.update(eventRef, { teams: nextTeams });
					return resolvedRegistrationID;
				});
				updateEventInState();
			}
			setPayTarget({
				athlete: {
					id: user.id,
					parentID: user.id,
					firstName: team.name,
					lastName: "Team",
					registrations: [
						{
							id: event.id,
							regID: paymentRegistrationID,
							status: "unpaid",
							paidAt: null,
						},
					],
				},
				event: {
					...event,
					isTeamFee: true,
					teamName: team.name,
					teamID: resolvedTeamID,
					teamPaymentRegistrationID: paymentRegistrationID,
				},
			});
		} catch (error: any) {
			setTeamActionError(
				error.message ?? "Could not start payment. Try again.",
			);
		}
	};
	const requestTeamPayment = async (
		event: DocumentData,
		team: AdultLeagueTeam,
	) => {
		setTeamActionError("");
		try {
			const signed = await hasSignedAdultLeagueWaiver(user.id, [event]);
			if (signed) {
				startTeamPayment(event, team);
				return;
			}
			setTeamPaymentWaiverTarget({ event, team });
		} catch (error: any) {
			setTeamActionError(
				error.message ?? "Could not check waiver status. Try again.",
			);
		}
	};
	const leaveTeam = async (event: DocumentData, team: AdultLeagueTeam) => {
		setTeamActionError("");
		if (
			isTeamPaid(team) ||
			!window.confirm(
				`Leave ${team.name}? If you are the last player on the roster, the unpaid team will be deleted.`,
			)
		) {
			return;
		}

		try {
			const eventRef = doc(db, "events", event.id);
			await runTransaction(db, async (transaction) => {
				const eventSnap = await transaction.get(eventRef);
				if (!eventSnap.exists()) {
					throw new Error("League not found.");
				}

				const currentTeams = (eventSnap.data().teams ?? []) as AdultLeagueTeam[];
				const currentUserTeam = currentTeams.find((thisTeam) =>
					isUserOnTeam(thisTeam, user.id),
				);
				if (!currentUserTeam) {
					throw new Error("You are not currently on a team in this league.");
				}
				if (isTeamPaid(currentUserTeam)) {
					throw new Error("Paid teams are locked.");
				}

				const nextRoster = (currentUserTeam.roster ?? []).filter(
					(member) => member.userID !== user.id,
				);
				const updatedTeams =
					nextRoster.length === 0
						? currentTeams.filter(
								(thisTeam) => getTeamID(thisTeam) !== getTeamID(currentUserTeam),
							)
						: currentTeams.map((thisTeam) =>
								getTeamID(thisTeam) === getTeamID(currentUserTeam)
									? { ...thisTeam, roster: nextRoster }
									: thisTeam,
							);

				if (nextRoster.length === 0 && currentUserTeam.paymentRegistrationID) {
					transaction.delete(
						doc(db, "registrations", currentUserTeam.paymentRegistrationID),
					);
				}

				transaction.update(eventRef, { teams: updatedTeams });
			});
			updateEventInState();
		} catch (error: any) {
			setTeamActionError(error.message ?? "Could not leave team. Try again.");
		}
	};
	const hasPaymentsDue =
		unpaidRegistrations.length > 0 || unpaidAdultTeamMemberships.length > 0;
	const renderPaymentsDuePanel = () =>
		hasPaymentsDue ? (
			<div className="grid gap-3 rounded-md border border-yellow-300/30 bg-yellow-500/10 p-3">
				<div>
					<h3 className="m-0 text-lg text-white">Payments Due</h3>
					<p className="m-0 text-sm text-yellow-100">
						Complete payment to confirm each registration.
					</p>
				</div>
				<div className="grid gap-2">
					{teamActionError ? (
						<div className="rounded-md border border-red-300/30 bg-red-500/15 p-3 text-sm text-red-100">
							{teamActionError}
						</div>
					) : null}
					{unpaidRegistrations.map(({ athlete, event, registration }) => (
						<div
							key={`${athlete.id}-${event.id}`}
							className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-white/10 bg-black/20 p-3"
						>
							<div>
								<div className="mb-1 flex flex-wrap items-center gap-2">
									<strong className="block text-white">{event.title}</strong>
									<RegisteredBadge tone="due" />
									<span className="rounded-full border border-yellow-300/30 bg-yellow-500/15 px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-yellow-100">
										Payment Due
									</span>
								</div>
								<RegistrationDetailLines
									athlete={athlete}
									event={event}
									registration={registration}
								/>
								<HatchlingsScheduleSummary event={event} compact />
							</div>
							<div className="flex flex-wrap gap-2">
								<button
									className={authClasses.buttonSmall}
									onClick={() => setPayTarget({ athlete, event })}
								>
									Pay
								</button>
								<button
									className={`${authClasses.buttonSmall} border-red-300/30 bg-red-500/15 text-red-100 hover:border-red-200/50 hover:bg-red-500/25`}
									onClick={() => setUnregisterTarget({ athlete, event })}
								>
									Withdraw
								</button>
							</div>
						</div>
					))}
					{unpaidAdultTeamMemberships.map((membership) => (
						<CurrentAdultMembershipCard
							key={`${membership.id}-team-fee`}
							copiedInviteKey={copiedInviteKey}
							copyingInviteKey={copyingInviteKey}
							membership={membership}
							onCopyInvite={copyTeamInviteLink}
							onLeave={leaveTeam}
							onPay={requestTeamPayment}
							renderFreeAgentInvitePanel={renderFreeAgentInvitePanel}
						/>
					))}
				</div>
			</div>
		) : null;

	if (loading) {
		return (
			<>
				<Header />
				<main className="min-h-[calc(100vh-64px)] bg-[#1c1d21] px-4 py-6 text-slate-100 md:px-8">
					<div className="mx-auto grid w-full max-w-5xl gap-6">
						<DashboardLoading message={loadingStage} />
					</div>
				</main>
			</>
		);
	}

	return (
		<>
		<Header
			dashboardNav={
				!addingAthlete
					? {
							activePage: activeDashboardPage,
							onPageChange: setDashboardPage,
						}
					: undefined
			}
		/>
		<main className="min-h-[calc(100vh-64px)] bg-[#1c1d21] px-4 py-6 text-slate-100 md:px-8">
			<div className="mx-auto grid w-full max-w-5xl gap-6">
				{loadError ? (
					<div className="rounded-lg border border-yellow-300/30 bg-yellow-500/10 p-4 text-sm text-yellow-100">
						<div className="font-semibold">{loadError}</div>
						<button
							className={`${authClasses.buttonSmall} mt-3`}
							type="button"
							onClick={fetchData}
						>
							Retry
						</button>
					</div>
				) : null}
				{registerTarget ? (
					<RegisterPopup
						athlete={registerTarget.athlete}
						evnt={registerTarget.event}
						setRegisterEvent={() => setRegisterTarget(null)}
						addEventRegistration={addEventRegistration}
						setPayEvent={(event: DocumentData) =>
							setPayTarget({ athlete: registerTarget.athlete, event })
						}
					/>
				) : null}
				{payTarget ? (
					<PayPopup
						athlete={payTarget.athlete}
						evnt={payTarget.event}
						setPayEvent={() => setPayTarget(null)}
					/>
				) : null}
				{unregisterTarget ? (
					<UnregisterPopup
						athlete={unregisterTarget.athlete}
						evnt={unregisterTarget.event}
						setUnregisterEvent={() => setUnregisterTarget(null)}
						removeEventRegistration={removeEventRegistration}
					/>
				) : null}
				{teamPaymentWaiverTarget ? (
					<AdultLeagueWaiverModal
						event={teamPaymentWaiverTarget.event}
						onCancel={() => setTeamPaymentWaiverTarget(null)}
						onSigned={() => {
							const target = teamPaymentWaiverTarget;
							setTeamPaymentWaiverTarget(null);
							if (target) {
								startTeamPayment(target.event, target.team);
							}
						}}
						user={user}
						userData={userData}
					/>
				) : null}

				{addingAthlete ? (
					<RegisterAthlete
						user={user}
						fetchAthletes={async () => {
							await fetchAthletes();
						}}
						setRegistering={setAddingAthlete}
					/>
					) : null}

					{!addingAthlete &&
					activeDashboardPage === "current" &&
					!hasTopPanel &&
					youthTeamAssignments.length === 0 ? (
						<section className="grid gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-5 text-slate-300">
							<div>
								<h2 className="m-0 text-2xl text-white">
									Current Registrations
								</h2>
								<p className="m-0 mt-2 text-sm text-slate-400">
									No current registrations yet. Use Register to browse open
									Sidewinder events.
								</p>
							</div>
							<button
								className={authClasses.buttonSmall}
								type="button"
								onClick={() => setDashboardPage("registrations")}
							>
								Browse registrations
							</button>
						</section>
					) : null}

						{!addingAthlete &&
						activeDashboardPage === "registrations" &&
						inviteLinkInvalid ? (
						<div className="rounded-lg border border-yellow-300/30 bg-yellow-500/10 p-4 text-sm text-yellow-100">
							{inviteUnavailableMessage}
						</div>
					) : null}

					{!addingAthlete &&
					activeDashboardPage === "current" &&
					hasTopPanel ? (
							<section className="grid gap-3">
						{renderPaymentsDuePanel()}

							{visiblePaidRegistrations.length > 0 ||
							paidAdultTeamMemberships.length > 0 ? (
								<div className="grid gap-3">
								<div>
									<h3 className="m-0 text-lg text-white">
										Registered Events
									</h3>
								</div>
								<div className="grid gap-2">
									{teamActionError ? (
										<div className="rounded-md border border-red-300/30 bg-red-500/15 p-3 text-sm text-red-100">
											{teamActionError}
										</div>
									) : null}
									{visiblePaidRegistrations.map(
										({ athlete, event, registration }) => {
											const seasonBadge = getSeasonBadgeLabel(event.season);
											const isHatchlings = event.type === "Hatchlings";

											return (
												<div
													key={`${athlete.id}-${registration.regID}`}
													className="rounded-md border border-white/10 bg-black/20 p-3"
												>
													<div className="flex flex-wrap items-start justify-between gap-3">
														<div className="min-w-0 flex-1">
															<div className="flex flex-wrap items-center gap-2">
																<strong className="text-white">
																	{event.title}
																</strong>
																<RegisteredBadge />
																{seasonBadge ? (
																	<span
																		className={`rounded-full border px-2 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.1em] ${getSeasonBadgeClass(
																			event.season,
																		)}`}
																	>
																		{seasonBadge}
																	</span>
																) : null}
															</div>
															<RegistrationDetailLines
																athlete={athlete}
																event={event}
																registration={registration}
															/>
															<HatchlingsScheduleSummary event={event} compact />
															{isHatchlings ? (
																<div className="mt-2 flex flex-wrap items-center gap-3">
																	<a
																		className={inviteLinkButtonClass}
																		href={hatchlingsUrl}
																		target="_blank"
																		rel="noopener noreferrer"
																	>
																		Hatchlings info
																	</a>
																</div>
															) : null}
														</div>
													</div>
												</div>
											);
										},
									)}
										{paidAdultTeamMemberships.map((membership) => (
											<CurrentAdultMembershipCard
												key={`${membership.id}-paid-team`}
												copiedInviteKey={copiedInviteKey}
												copyingInviteKey={copyingInviteKey}
												membership={membership}
												onCopyInvite={copyTeamInviteLink}
												renderFreeAgentInvitePanel={renderFreeAgentInvitePanel}
											/>
										))}
								</div>
							</div>
						) : null}
					</section>
				) : null}

					{!addingAthlete &&
					activeDashboardPage === "current" &&
					youthTeamAssignments.length > 0 ? (
						<>
							<YouthTeamsPage assignments={youthTeamAssignments} />
							<TeamPracticeCalendar assignments={youthTeamAssignments} />
						</>
					) : null}

					{!addingAthlete && activeDashboardPage === "registrations" ? (
						<section className="grid gap-4">
						{renderPaymentsDuePanel()}
						<div className="flex flex-wrap items-start justify-between gap-3">
								<div>
									<h2 className="m-0 text-2xl text-white">
										Available Registrations
									</h2>
									<p className="m-0 text-sm text-slate-400">
										{eventType === "League"
											? "Adult leagues use the signed-in adult. Kids leagues use a child athlete."
											: selectedEventSection.requiresChildAthlete
												? "Choose an event first, then select an eligible child athlete."
												: "Adult leagues will use the signed-in adult account."}
									</p>
								</div>
								{(selectedEventSection.requiresChildAthlete ||
									(eventType === "League" && hasKidsLeagueInventory)) &&
								athletes.length > 0 ? (
									<button
										className="rounded-md border border-white/10 bg-black/20 px-3 py-2 text-sm font-bold text-slate-300 transition hover:border-accent-color-2 hover:text-accent-color-2"
										onClick={() => setAddingAthlete(true)}
								>
									Add Athlete
								</button>
							) : null}
						</div>
						<div className="flex flex-wrap gap-2">
							{eventSections.map((section) => (
								<button
									key={section.value}
									className={`rounded-md border px-3 py-2 text-sm font-bold transition ${
										eventType === section.value
											? "border-accent-color-1 bg-accent-color-1 text-white"
											: "border-white/10 bg-black/20 text-slate-300 hover:border-accent-color-2 hover:text-accent-color-2"
									}`}
									type="button"
									onClick={() => {
										setUserSelectedEventType(true);
										setEventType(section.value);
									}}
								>
									{section.label}
								</button>
							))}
						</div>
						{eventType === "Clinic" ? (
							<div className="flex flex-wrap gap-2 rounded-lg border border-white/10 bg-white/[0.03] p-2">
								{clinicFilters.map((filter) => (
									<button
										key={filter.value}
										className={`rounded-md border px-3 py-2 text-sm font-bold transition ${
											clinicFilter === filter.value
												? "border-accent-color-2 bg-accent-color-2 text-[#101216]"
												: "border-white/10 bg-black/20 text-slate-300 hover:border-accent-color-2 hover:text-accent-color-2"
										}`}
										type="button"
										onClick={() => setClinicFilter(filter.value)}
									>
										{filter.label}
									</button>
								))}
							</div>
						) : null}

							{selectedAvailableEventsError ? (
								<div className="grid gap-3 rounded-lg border border-yellow-300/30 bg-yellow-500/10 p-5 text-yellow-100">
									<p className="m-0 text-sm font-semibold">
										{selectedAvailableEventsError}
								</p>
								<button
									className={authClasses.buttonSmall}
									type="button"
									onClick={() => {
										fetchAvailableEvents(eventType).catch(() => {
											// Error state is handled in useDashboardData.
										});
									}}
								>
									Retry
								</button>
							</div>
							) : !selectedAvailableEventsLoaded || selectedAvailableEventsLoading ? (
								<div className="rounded-lg border border-white/10 bg-white/[0.03] p-5 text-slate-300">
									Loading available registrations...
								</div>
							) : eventType === "League" ? (
								<div className="grid gap-5">
									{availableEvents.length === 0 &&
									!(hasKidsLeagueInventory && athletes.length === 0) ? (
										<div className="rounded-lg border border-white/10 bg-white/[0.03] p-5 text-slate-300">
											No leagues are available right now.
										</div>
									) : null}

									{hasKidsLeagueInventory && athletes.length === 0 ? (
										<div className="grid gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-5 text-slate-300">
											<p className="m-0">
												Add a child athlete before registering for kids leagues.
											</p>
											<button
												className={authClasses.button}
												onClick={() => setAddingAthlete(true)}
											>
												Add Child Athlete
											</button>
										</div>
									) : null}

									{kidsLeagueEvents.length > 0 ? (
										<div className="grid gap-3">
											<h3 className="m-0 text-lg text-white">Kids Leagues</h3>
											{inProgressKidsLeagueEvents.length > 0 ? (
												<div className="grid gap-3">
													<h4 className="m-0 text-sm font-semibold uppercase tracking-[0.12em] text-slate-400">
														In Progress
													</h4>
													{renderKidsLeagueCards(inProgressKidsLeagueEvents)}
												</div>
											) : null}
											{futureKidsLeagueEvents.length > 0 ? (
												<div className="grid gap-3">
													<h4 className="m-0 text-sm font-semibold uppercase tracking-[0.12em] text-slate-400">
														Future
													</h4>
													{renderKidsLeagueCards(futureKidsLeagueEvents)}
												</div>
											) : null}
										</div>
									) : null}

									{adultLeagueEvents.length > 0 ? (
										<div className="grid gap-3">
											<h3 className="m-0 text-lg text-white">Adult Leagues</h3>
											{!adultProfileSaved ? (
												<AdultProfileGate
													user={user}
													userData={userData}
													onProfileSaved={async () => {
														setAdultProfileSaved(true);
														await fetchData();
													}}
												/>
											) : (
												<AdultLeagueWaiverGate
													adultEvents={adultLeagueEvents}
													user={user}
													userData={userData}
												>
													<div className="grid gap-5">
														{inProgressAdultLeagueEvents.length > 0 ? (
															<div className="grid gap-3">
																<h4 className="m-0 text-sm font-semibold uppercase tracking-[0.12em] text-slate-400">
																	In Progress
																</h4>
																{renderAdultLeagueCards(
																	inProgressAdultLeagueEvents,
																)}
															</div>
														) : null}
														{futureAdultLeagueEvents.length > 0 ? (
															<div className="grid gap-3">
																<h4 className="m-0 text-sm font-semibold uppercase tracking-[0.12em] text-slate-400">
																	Future
																</h4>
																{renderAdultLeagueCards(futureAdultLeagueEvents)}
															</div>
														) : null}
													</div>
												</AdultLeagueWaiverGate>
											)}
										</div>
									) : null}
								</div>
							) : selectedEventSection.requiresChildAthlete &&
							athletes.length === 0 ? (
							<div className="grid gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-5 text-slate-300">
								<p className="m-0">
									Add a child athlete before registering for youth events. Adult
									league registration will not require this step.
								</p>
								<button
									className={authClasses.button}
									onClick={() => setAddingAthlete(true)}
								>
									Add Child Athlete
								</button>
							</div>
						) : availableEvents.length === 0 ? (
							<div className="rounded-lg border border-white/10 bg-white/[0.03] p-5 text-slate-300">
								{`No available ${selectedEventSection.label.toLowerCase()} registrations right now.`}
							</div>
						) : eventType === "Tryout" ? (
							<div className="grid gap-5">
								{tryoutEventGroups.filter((group) => group.events.length > 0).map((group) => (
									<section key={group.title} className="grid gap-3" aria-label={group.title}>
										<h4 className="m-0 text-sm font-semibold uppercase tracking-[0.12em] text-slate-400">
											{group.title}
										</h4>
										{group.events.map((event) => (
											<EventFirstCard
												key={event.id}
												event={event}
												athletes={athletes}
												onRegister={setRegisterTarget}
											/>
										))}
									</section>
								))}
							</div>
						) : (
							availableEvents.map((event) => (
								<EventFirstCard
									key={event.id}
									event={event}
									athletes={athletes}
									onRegister={setRegisterTarget}
								/>
							))
						)}
					</section>
				) : null}
			</div>
		</main>
		</>
	);
};

export default EventFirstDashboard;
